import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { BlockMath } from "react-katex";
import ConfirmDialog from "../components/ConfirmDialog";

const STEP_TAG_OPTIONS = [
  "setup",
  "formula_application",
  "identity",
  "substitution",
  "elimination",
  "transformation",
  "simplification",
  "arithmetic",
  "result",
  "assumption",
  "statement",
  "other"
];

const EDGE_KIND_OPTIONS = [
  { value: "required", label: "Required" },
  { value: "optional", label: "Optional" },
  { value: "alternate", label: "Alternate" }
];

const PATH_COLORS = ["#2563eb", "#0f766e", "#b45309", "#7c3aed", "#dc2626", "#0891b2"];
const NODE_WIDTH = 264;
const NODE_HEIGHT = 156;

function createClientId(prefix) {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID().replace(/-/g, "").slice(0, 12)}`;
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 14)}`;
}

function getDefaultParamValue(paramDef) {
  if (paramDef?.defaultValue !== undefined) return paramDef.defaultValue;
  if (paramDef?.valueType === "boolean") return false;
  if (paramDef?.valueType === "string[]") return [];
  return "";
}

function getValidatorGroupsForKind(catalog, nodeKind) {
  return (catalog || []).filter((group) => (group.supportedNodeKinds || []).includes(nodeKind));
}

function buildDefaultValidator(nodeKind, catalog) {
  const groups = getValidatorGroupsForKind(catalog, nodeKind);
  const firstGroup = groups[0];
  const firstRule = firstGroup?.rules?.[0];
  if (!firstGroup || !firstRule) {
    return { type: "", ruleId: "", params: {} };
  }
  const params = {};
  (firstRule.params || []).forEach((paramDef) => {
    params[paramDef.key] = getDefaultParamValue(paramDef);
  });
  return {
    type: firstGroup.type,
    ruleId: firstRule.ruleId,
    params
  };
}

function findRuleDefinition(catalog, type, ruleId) {
  const group = (catalog || []).find((item) => item.type === type);
  if (!group) return { group: null, rule: null };
  const rule = (group.rules || []).find((item) => item.ruleId === ruleId) || null;
  return { group, rule };
}

function isSupportingDeterministicRule(type, ruleId) {
  return String(type || "").trim() === "deterministic_rule" && String(ruleId || "").trim() === "point_on_axis_assumption";
}

function buildSpecializedNodeDefaults(type, ruleId, params = {}) {
  if (String(type || "").trim() === "deterministic_rule" && String(ruleId || "").trim() === "point_on_axis_assumption") {
    const axis = String(params?.axis || "x_axis").trim();
    const pointLabel = String(params?.pointLabel || "P").trim() || "P";
    const freeCoordinateSymbol = String(params?.freeCoordinateSymbol || (axis === "y_axis" ? "y" : "x")).trim() || "x";
    const tuple = axis === "y_axis" ? `(0, ${freeCoordinateSymbol})` : `(${freeCoordinateSymbol}, 0)`;
    const axisLabel = axis === "y_axis" ? "y-axis" : "x-axis";
    return {
      title: `Assume the point on the ${axisLabel}`,
      description: `Introduce the unknown point on the ${axisLabel} as ${pointLabel} = ${tuple}.`,
      conceptTags: ["assumption", "variable_statement", "coordinate_geometry"],
      anchorStepTag: "assumption",
      anchorLatex: `${pointLabel} = ${tuple}`,
      hintSeed: `Let the point on the ${axisLabel} be ${pointLabel} = ${tuple}.`,
      requiredForProgress: false
    };
  }

  return null;
}

function formatEdgeDisplay(edge, nodes) {
  const allNodes = Array.isArray(nodes) ? nodes : [];
  const fromNode = allNodes.find((node) => String(node?.nodeId || "") === String(edge?.fromNodeId || ""));
  const toNode = allNodes.find((node) => String(node?.nodeId || "") === String(edge?.toNodeId || ""));
  const fromLabel = String(fromNode?.title || edge?.fromNodeId || "").trim();
  const toLabel = String(toNode?.title || edge?.toNodeId || "").trim();
  return `${fromLabel} -> ${toLabel}`;
}

function buildNodeTemplateOptions(catalog) {
  const options = [
    { value: "blank_canonical", label: "Blank Canonical Node" },
    { value: "blank_deterministic", label: "Blank Deterministic Node" }
  ];

  (catalog || []).forEach((group) => {
    (group.rules || []).forEach((rule) => {
      const nodeKind = (group.supportedNodeKinds || [])[0] || "canonical";
      options.push({
        value: `rule:${group.type}:${rule.ruleId}:${nodeKind}`,
        label: `${group.label}: ${rule.label}`
      });
    });
  });

  return options;
}

const RUNTIME_MODE_OPTIONS = [
  {
    value: "solution_graph",
    label: "Solution Graph",
    summary: "Use authored nodes, validators, paths, and graph matching."
  },
  {
    value: "ai_runtime_spec",
    label: "AI Runtime Spec",
    summary: "Use frame-specific AI guardrails for hints, errors, and analyze while graph authoring catches up."
  }
];

const AI_RUNTIME_SPEC_FIELDS = [
  {
    key: "problemFacts",
    label: "Problem Facts",
    placeholder: "Facts AI must treat as true for this frame. Example: Triangle PQR is right-angled at Q."
  },
  {
    key: "acceptedClaims",
    label: "Accepted Correct Claims",
    placeholder: "Valid shortcuts, conventions, or fragile steps that must not be called wrong."
  },
  {
    key: "commonErrors",
    label: "Common Errors",
    placeholder: "Likely mistakes to watch for. These are suspicion signals, not proof."
  },
  {
    key: "errorGuardrails",
    label: "Error Guardrails",
    placeholder: "When AI may or may not show an error. Example: If side labels are ambiguous, ask a check question."
  },
  {
    key: "hintPolicy",
    label: "Hint Policy",
    placeholder: "How hints should be given. Example: One next move only; no final answer reveal."
  },
  {
    key: "analyzePolicy",
    label: "Analyze Policy",
    placeholder: "How final attempt review should judge correctness for this frame."
  }
];

function normalizeRuntimeMode(value) {
  return String(value || "solution_graph").trim() === "ai_runtime_spec" ? "ai_runtime_spec" : "solution_graph";
}

function normalizeRuntimeSpecList(value, options = {}) {
  const trimLines = options.trim !== false;
  if (Array.isArray(value)) {
    return value
      .map((item) => (trimLines ? String(item || "").trim() : String(item || "")))
      .filter((item) => String(item || "").trim().length > 0);
  }
  return String(value || "")
    .split(/\r?\n/)
    .map((item) => (trimLines ? item.trim() : item))
    .filter((item) => String(item || "").trim().length > 0);
}

function runtimeSpecListToText(value) {
  if (Array.isArray(value)) return value.map((item) => String(item || "")).join("\n");
  return String(value || "");
}

function createDefaultAiRuntimeSpec(frame = null) {
  const facts = [];
  if (frame?.title) facts.push(`Frame title: ${frame.title}`);
  if (frame?.difficulty) facts.push(`Difficulty: ${frame.difficulty}`);
  if (frame?.latexTemplate) facts.push(`Question template: ${frame.latexTemplate}`);

  return {
    problemFacts: facts,
    acceptedClaims: [],
    commonErrors: [],
    errorGuardrails: [
      "Accept mathematically equivalent steps, valid naming conventions, and valid skipped algebra steps.",
      "If OCR or side-label interpretation is ambiguous, ask a check question instead of showing an error.",
      "Show an error only when there is a high-confidence local contradiction with the question."
    ],
    hintPolicy: [
      "Give one next move only.",
      "Do not reveal the final answer unless the student has already completed it.",
      "Use the student's own symbols and numbers."
    ],
    analyzePolicy: [
      "Judge correctness from the original question, not from one preferred canonical sequence.",
      "Accept valid alternate paths and valid intermediate simplifications."
    ],
    responseStyle: {
      maxWords: 55,
      noSolutionReveal: true,
      oneNextMoveOnly: true
    }
  };
}

function normalizeAiRuntimeSpecDraft(spec, frame = null, options = {}) {
  const defaults = createDefaultAiRuntimeSpec(frame);
  const source = spec && typeof spec === "object" ? spec : {};
  const responseStyle = source.responseStyle && typeof source.responseStyle === "object" ? source.responseStyle : {};
  const maxWords = Number(responseStyle.maxWords);
  const listOptions = { trim: options.preserveText !== true };
  return {
    problemFacts: normalizeRuntimeSpecList(source.problemFacts || defaults.problemFacts, listOptions),
    acceptedClaims: normalizeRuntimeSpecList(source.acceptedClaims, listOptions),
    commonErrors: normalizeRuntimeSpecList(source.commonErrors, listOptions),
    errorGuardrails: normalizeRuntimeSpecList(source.errorGuardrails || defaults.errorGuardrails, listOptions),
    hintPolicy: normalizeRuntimeSpecList(source.hintPolicy || defaults.hintPolicy, listOptions),
    analyzePolicy: normalizeRuntimeSpecList(source.analyzePolicy || defaults.analyzePolicy, listOptions),
    responseStyle: {
      maxWords: Number.isFinite(maxWords) ? Math.max(25, Math.min(120, maxWords)) : defaults.responseStyle.maxWords,
      noSolutionReveal: responseStyle.noSolutionReveal !== false,
      oneNextMoveOnly: responseStyle.oneNextMoveOnly !== false
    }
  };
}

function hasAiRuntimeSpecContent(spec) {
  const source = spec && typeof spec === "object" ? spec : {};
  return AI_RUNTIME_SPEC_FIELDS.some((field) => normalizeRuntimeSpecList(source[field.key]).length > 0);
}

function isAutoRuntimeProblemFact(value) {
  return /^(Frame title|Difficulty|Question template):/i.test(String(value || "").trim());
}

function reframeAiRuntimeSpec(spec, frame = null) {
  const normalized = normalizeAiRuntimeSpecDraft(spec, frame);
  const currentFacts = normalizeRuntimeSpecList(spec?.problemFacts);
  const hasCustomFacts = currentFacts.some((fact) => !isAutoRuntimeProblemFact(fact));
  return {
    ...normalized,
    problemFacts: hasCustomFacts ? currentFacts : createDefaultAiRuntimeSpec(frame).problemFacts
  };
}
const GOAL_COLORS = ["#7c3aed", "#0f766e", "#b45309", "#0891b2", "#dc2626", "#2563eb"];
const EMIT_EXTRACT_OPTIONS = [
  { value: "rhs_value",       label: "RHS numeric value" },
  { value: "rhs_latex",       label: "RHS LaTeX string" },
  { value: "numeric_value",   label: "Full expression numeric" },
  { value: "fromExpression",  label: "From expression (context)" }
];

// Accepts array ["a","b"] or object {"a":"desc"} — returns [{name,description}]
function normalizeContractDisplay(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map((n) => ({ name: String(n), description: "" }));
  if (typeof value === "object") return Object.entries(value).map(([name, desc]) => ({ name, description: String(desc || "") }));
  return [];
}

function createBlankPath(index) {
  return {
    pathId: createClientId("path"),
    title: `Path ${index}`,
    description: "",
    nodeSequence: [],
    goalSequence: [],
    isPrimaryReviewPath: index === 1,
    ui: {
      color: PATH_COLORS[(index - 1) % PATH_COLORS.length]
    }
  };
}

function createBlankGoal(index) {
  return {
    goalId: createClientId("goal"),
    title: `Goal ${index}`,
    nodeIds: [],
    keyGoal: false,
    requiresGoals: []
  };
}

function createBlankNode(catalog, defaultPathId, index) {
  return {
    nodeId: createClientId("node"),
    title: `Node ${index}`,
    description: "",
    kind: "canonical",
    keyStep: false,
    requiredForProgress: true,
    conceptTags: [],
    pathIds: defaultPathId ? [defaultPathId] : [],
    reviewOrder: index,
    validator: buildDefaultValidator("canonical", catalog),
    anchor: {
      latex: "",
      stepTag: "other",
      keyStep: false
    },
    hint: {
      mode: "next_step",
      seed: ""
    },
    ui: {
      color: "",
      collapsed: false
    },
    position: {
      x: 48 + ((index - 1) % 3) * 320,
      y: 48 + Math.floor((index - 1) / 3) * 220
    }
  };
}

function createNodeFromTemplate(catalog, defaultPathId, index, templateKey) {
  const normalizedTemplate = String(templateKey || "blank_canonical");
  if (normalizedTemplate === "blank_deterministic") {
    const node = createBlankNode(catalog, defaultPathId, index);
    const validator = buildDefaultValidator("deterministic", catalog);
    const isAssumptionDefault = isSupportingDeterministicRule(validator.type, validator.ruleId);
    const specializedDefaults = buildSpecializedNodeDefaults(validator.type, validator.ruleId, validator.params);
    return {
      ...node,
      title: specializedDefaults?.title || `Deterministic Node ${index}`,
      description: specializedDefaults?.description || node.description,
      kind: "deterministic",
      requiredForProgress: specializedDefaults?.requiredForProgress ?? (isAssumptionDefault ? false : node.requiredForProgress),
      conceptTags: specializedDefaults?.conceptTags || node.conceptTags,
      anchor: {
        ...node.anchor,
        latex: specializedDefaults?.anchorLatex || node.anchor?.latex || "",
        stepTag: specializedDefaults?.anchorStepTag || (isAssumptionDefault ? "assumption" : "")
      },
      hint: specializedDefaults?.hintSeed
        ? {
            mode: "next_step",
            seed: specializedDefaults.hintSeed
          }
        : isAssumptionDefault
          ? {
              mode: "next_step",
              seed: "Let the point on the x-axis be P = (x, 0)."
            }
          : node.hint,
      validator
    };
  }
  if (!normalizedTemplate.startsWith("rule:")) {
    return createBlankNode(catalog, defaultPathId, index);
  }

  const [, type, ruleId, nodeKind = "canonical"] = normalizedTemplate.split(":");
  const { group, rule } = findRuleDefinition(catalog, type, ruleId);
  if (!group || !rule) {
    return createBlankNode(catalog, defaultPathId, index);
  }

  const params = {};
  (rule.params || []).forEach((paramDef) => {
    params[paramDef.key] = getDefaultParamValue(paramDef);
  });

  const baseNode = createBlankNode(catalog, defaultPathId, index);
  const isAssumptionRule = type === "deterministic_rule" && ruleId === "point_on_axis_assumption";
  const specializedDefaults = buildSpecializedNodeDefaults(type, ruleId, params);
  return {
    ...baseNode,
    title: specializedDefaults?.title || rule.label,
    description: specializedDefaults?.description || baseNode.description,
    kind: nodeKind,
    requiredForProgress: specializedDefaults?.requiredForProgress ?? (isAssumptionRule ? false : baseNode.requiredForProgress),
    conceptTags: specializedDefaults?.conceptTags || (isAssumptionRule ? ["assumption", "variable_statement"] : [rule.ruleId]),
    anchor: {
      ...baseNode.anchor,
      latex: specializedDefaults?.anchorLatex || baseNode.anchor?.latex || "",
      stepTag:
        nodeKind === "canonical"
          ? baseNode.anchor.stepTag
          : specializedDefaults?.anchorStepTag || (isAssumptionRule ? "assumption" : "")
    },
    hint: specializedDefaults?.hintSeed
      ? {
          mode: "next_step",
          seed: specializedDefaults.hintSeed
        }
      : isAssumptionRule
        ? {
            mode: "next_step",
            seed: "Let the point on the x-axis be P = (x, 0)."
          }
        : baseNode.hint,
    validator: {
      type,
      ruleId,
      params
    }
  };
}

function createBlankGraph(catalog, frameId = "", frameTitle = "", opts = {}) {
  const firstPath = createBlankPath(1);
  return {
    _id: null,
    isLibraryGraph: opts.isLibraryGraph === true,
    frameId: opts.isLibraryGraph ? null : frameId,
    chapterId: opts.chapterId || "",
    difficulty: opts.difficulty || "HOT",
    title: opts.isLibraryGraph
      ? (opts.title || "")
      : (frameTitle ? `${frameTitle} Solution Graph` : ""),
    description: "",
    version: 1,
    status: "draft",
    activeRuntimeMode: "solution_graph",
    aiRuntimeSpec: createDefaultAiRuntimeSpec(opts.sourceFrame || null),
    primaryReviewPathId: firstPath.pathId,
    paths: [firstPath],
    nodes: [],
    edges: [],
    goals: [],
    validationErrors: [],
    validationWarnings: [],
    graphStats: {
      nodeCount: 0,
      keyStepCount: 0,
      canonicalNodeCount: 0,
      deterministicNodeCount: 0,
      pathCount: 1,
      edgeCount: 0,
      publishReady: false
    },
    generationTrace: {
      source: "manual",
      promptTemplate: "",
      renderedPrompt: "",
      model: "",
      location: "",
      usageMetadata: null,
      generatedAt: null
    }
  };
}

function hydrateGraph(serverGraph, catalog) {
  if (!serverGraph) return null;
  const paths = Array.isArray(serverGraph.paths)
    ? serverGraph.paths.map((path, index) => ({
        ...path,
        ui: {
          color: path?.ui?.color || PATH_COLORS[index % PATH_COLORS.length]
        },
        nodeSequence: Array.isArray(path?.nodeSequence) ? path.nodeSequence : [],
        goalSequence: Array.isArray(path?.goalSequence) ? path.goalSequence : [],
        isPrimaryReviewPath:
          path.isPrimaryReviewPath === true || String(serverGraph.primaryReviewPathId || "") === String(path.pathId || "")
      }))
    : [];

  const pathMembership = new Map();
  paths.forEach((path) => {
    (path.nodeSequence || []).forEach((nodeId) => {
      if (!pathMembership.has(nodeId)) pathMembership.set(nodeId, []);
      pathMembership.get(nodeId).push(path.pathId);
    });
  });

  const nodes = Array.isArray(serverGraph.nodes)
    ? serverGraph.nodes.map((node, index) => {
        const nodeKind = String(node?.kind || "canonical");
        const fallbackValidator = buildDefaultValidator(nodeKind, catalog);
        return {
          ...node,
          requiredForProgress: node?.requiredForProgress !== false,
          conceptTags: Array.isArray(node?.conceptTags) ? node.conceptTags : [],
          pathIds:
            Array.isArray(node?.pathIds) && node.pathIds.length
              ? node.pathIds
              : pathMembership.get(node.nodeId) || [],
          validator:
            node?.validator?.type && node?.validator?.ruleId
              ? node.validator
              : fallbackValidator,
          anchor: {
            latex: String(node?.anchor?.latex || "").trim(),
            stepTag: String(node?.anchor?.stepTag || (nodeKind === "canonical" ? "other" : "")).trim(),
            keyStep: node?.anchor?.keyStep === true
          },
          hint: {
            mode: String(node?.hint?.mode || "next_step"),
            seed: String(node?.hint?.seed || "")
          },
          ui: {
            color: String(node?.ui?.color || ""),
            collapsed: node?.ui?.collapsed === true
          },
          position: {
            x: Number.isFinite(Number(node?.position?.x)) ? Number(node.position.x) : 48 + (index % 3) * 320,
            y: Number.isFinite(Number(node?.position?.y)) ? Number(node.position.y) : 48 + Math.floor(index / 3) * 220
          }
        };
      })
    : [];

  const goals = Array.isArray(serverGraph.goals)
    ? serverGraph.goals.map((goal) => ({
        goalId: String(goal?.goalId || ""),
        title: String(goal?.title || ""),
        nodeIds: Array.isArray(goal?.nodeIds) ? goal.nodeIds : [],
        keyGoal: goal?.keyGoal === true,
        requiresGoals: Array.isArray(goal?.requiresGoals) ? goal.requiresGoals : []
      }))
    : [];

  const populatedFrame = serverGraph.frameId && typeof serverGraph.frameId === "object" ? serverGraph.frameId : null;

  return {
    ...serverGraph,
    frameId: serverGraph.frameId?._id || serverGraph.frameId || "",
    activeRuntimeMode: normalizeRuntimeMode(serverGraph.activeRuntimeMode),
    aiRuntimeSpec: normalizeAiRuntimeSpecDraft(serverGraph.aiRuntimeSpec, populatedFrame),
    primaryReviewPathId:
      String(serverGraph.primaryReviewPathId || "") ||
      paths.find((path) => path.isPrimaryReviewPath)?.pathId ||
      "",
    paths,
    nodes,
    edges: Array.isArray(serverGraph.edges) ? serverGraph.edges : [],
    goals,
    validationErrors: Array.isArray(serverGraph.validationErrors) ? serverGraph.validationErrors : [],
    validationWarnings: Array.isArray(serverGraph.validationWarnings) ? serverGraph.validationWarnings : [],
    graphStats: serverGraph.graphStats || {}
  };
}

function computeGraphStats(draft) {
  const nodes = Array.isArray(draft?.nodes) ? draft.nodes : [];
  const paths = Array.isArray(draft?.paths) ? draft.paths : [];
  const edges = Array.isArray(draft?.edges) ? draft.edges : [];
  const goals = Array.isArray(draft?.goals) ? draft.goals : [];
  return {
    nodeCount: nodes.length,
    keyStepCount: nodes.filter((node) => node.keyStep).length,
    canonicalNodeCount: nodes.filter((node) => node.kind === "canonical").length,
    deterministicNodeCount: nodes.filter((node) => node.kind === "deterministic").length,
    pathCount: paths.length,
    edgeCount: edges.length,
    goalCount: goals.length,
    publishReady: false
  };
}

function buildPayloadFromDraft(draft) {
  const nodes = Array.isArray(draft.nodes) ? draft.nodes : [];
  const goals = Array.isArray(draft.goals) ? draft.goals : [];
  const paths = (Array.isArray(draft.paths) ? draft.paths : []).map((path) => {
    const nodeSequence = nodes
      .filter((node) => Array.isArray(node.pathIds) && node.pathIds.includes(path.pathId))
      .sort((left, right) => {
        const leftOrder = Number.isFinite(Number(left.reviewOrder)) ? Number(left.reviewOrder) : Number.MAX_SAFE_INTEGER;
        const rightOrder = Number.isFinite(Number(right.reviewOrder)) ? Number(right.reviewOrder) : Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) return leftOrder - rightOrder;
        return String(left.title || left.nodeId || "").localeCompare(String(right.title || right.nodeId || ""));
      })
      .map((node) => node.nodeId);

    return {
      pathId: path.pathId,
      title: path.title,
      description: path.description || "",
      nodeSequence,
      goalSequence: Array.isArray(path.goalSequence) ? path.goalSequence : [],
      isPrimaryReviewPath: path.isPrimaryReviewPath === true,
      ui: path.ui || { color: "" }
    };
  });

  const primaryReviewPathId =
    paths.find((path) => path.isPrimaryReviewPath)?.pathId || String(draft.primaryReviewPathId || "").trim();

  return {
    isLibraryGraph: draft.isLibraryGraph === true,
    frameId: draft.frameId || undefined,
    chapterId: draft.isLibraryGraph ? (draft.chapterId || "") : undefined,
    difficulty: draft.isLibraryGraph ? (draft.difficulty || "HOT") : undefined,
    inputVariables: draft.isLibraryGraph ? (draft.inputVariables || []) : undefined,
    outputVariables: draft.isLibraryGraph ? (draft.outputVariables || []) : undefined,
    title: draft.title,
    description: draft.description || "",
    version: Number(draft.version) || 1,
    status: draft.status || "draft",
    activeRuntimeMode: normalizeRuntimeMode(draft.activeRuntimeMode),
    aiRuntimeSpec: normalizeAiRuntimeSpecDraft(draft.aiRuntimeSpec, null),
    graphBundleVersion: 1,
    primaryReviewPathId,
    paths,
    nodes: nodes.map((node) => {
      const anchor = node.anchor || { latex: "", stepTag: "", keyStep: false };
      const validator = {
        ...(node.validator || { type: "", ruleId: "", params: {} }),
        params: {
          ...((node.validator && node.validator.params) || {})
        }
      };

      if (node.kind === "canonical" && validator.type === "equivalence") {
        if (String(anchor.latex || "").trim()) {
          validator.params.anchorLatex = anchor.latex;
        }
        if (validator.ruleId === "expression_anchor") {
          validator.params.stepTag = anchor.stepTag || "other";
        } else if (Object.prototype.hasOwnProperty.call(validator.params, "stepTag")) {
          delete validator.params.stepTag;
        }
      }

      return {
        nodeId: node.nodeId,
        title: node.title,
        description: node.description || "",
        kind: node.kind,
        keyStep: node.keyStep === true,
        requiredForProgress: node.requiredForProgress !== false,
        conceptTags: Array.isArray(node.conceptTags) ? node.conceptTags : [],
        pathIds: Array.isArray(node.pathIds) ? node.pathIds : [],
        reviewOrder: Number.isFinite(Number(node.reviewOrder)) ? Number(node.reviewOrder) : null,
        validator,
        anchor,
        hint: node.hint || { mode: "next_step", seed: "" },
        emits: (node.emits && typeof node.emits === "object" && Object.keys(node.emits).length > 0)
          ? node.emits
          : null,
        assigns: (node.assigns && typeof node.assigns === "object" && Object.keys(node.assigns).length > 0)
          ? node.assigns
          : null,
        condition: String(node.condition || "").trim() || null,
        ui: node.ui || { color: "", collapsed: false },
        position: node.position || { x: 0, y: 0 }
      };
    }),
    edges: (Array.isArray(draft.edges) ? draft.edges : []).map((edge) => ({
      edgeId: edge.edgeId,
      fromNodeId: edge.fromNodeId,
      toNodeId: edge.toNodeId,
      kind: edge.kind || "required",
      label: edge.label || ""
    })),
    goals: goals.map((goal) => ({
      goalId: goal.goalId,
      title: goal.title,
      nodeIds: Array.isArray(goal.nodeIds) ? goal.nodeIds : [],
      keyGoal: goal.keyGoal === true,
      requiresGoals: Array.isArray(goal.requiresGoals) ? goal.requiresGoals : []
    }))
  };
}

function parseJsonText(input, fallback = {}) {
  const raw = String(input || "").trim();
  if (!raw) {
    return { ok: true, value: fallback };
  }
  try {
    const parsed = JSON.parse(raw);
    return {
      ok: true,
      value: parsed && typeof parsed === "object" ? parsed : fallback
    };
  } catch (err) {
    return {
      ok: false,
      value: fallback,
      error: err.message
    };
  }
}

function shouldHideValidatorParam(node, paramDef) {
  return (
    node?.kind === "canonical" &&
    node?.validator?.type === "equivalence" &&
    ["stepTag", "anchorLatex"].includes(String(paramDef?.key || ""))
  );
}

function formatAnchorPreviewForCard(input) {
  let text = String(input || "").trim();
  if (!text) return "No anchor yet";

  const greekMap = {
    theta: "theta",
    alpha: "alpha",
    beta: "beta",
    gamma: "gamma",
    delta: "delta",
    lambda: "lambda",
    mu: "mu",
    pi: "pi",
    sigma: "sigma",
    phi: "phi",
    omega: "omega"
  };

  let previous = "";
  while (previous !== text) {
    previous = text;
    text = text.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "($1)/($2)");
    text = text.replace(/\\sqrt\{([^{}]+)\}/g, "sqrt($1)");
  }

  text = text
    .replace(/\\left|\\right/g, "")
    .replace(/\\cdot|\\times/g, " * ")
    .replace(/\\div/g, " / ")
    .replace(/\\to/g, " -> ")
    .replace(/\\leq/g, " <= ")
    .replace(/\\geq/g, " >= ")
    .replace(/\\neq/g, " != ")
    .replace(/\\pm/g, " +/- ")
    .replace(/\\operatorname\{([^{}]+)\}/g, "$1")
    .replace(/\\text\{([^{}]+)\}/g, "$1")
    .replace(/\\,/g, " ")
    .replace(/\\;/g, " ")
    .replace(/\\:/g, " ")
    .replace(/\\!/g, "")
    .replace(/\\([a-zA-Z]+)\b/g, (_, command) => greekMap[command] || command)
    .replace(/\^\{([^{}]+)\}/g, "^($1)")
    .replace(/_\{([^{}]+)\}/g, "_($1)")
    .replace(/[{}]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return text || "No anchor yet";
}

function SolutionGraphValidationStack({ draft }) {
  return (
    <div className="solution-graph-validation-stack">
      {draft.validationErrors?.length ? (
        <div className="solution-graph-validation-card error">
          <strong>Errors</strong>
          {draft.validationErrors.map((item, index) => (
            <div key={`error-${index}`}>{item}</div>
          ))}
        </div>
      ) : null}
      {draft.validationWarnings?.length ? (
        <div className="solution-graph-validation-card warning">
          <strong>Warnings</strong>
          {draft.validationWarnings.map((item, index) => (
            <div key={`warning-${index}`}>{item}</div>
          ))}
        </div>
      ) : null}
      {!draft.validationErrors?.length && !draft.validationWarnings?.length ? (
        <div className="solution-graph-empty">No validation issues stored yet.</div>
      ) : null}
    </div>
  );
}

function SolutionGraphDashboard({ draft, frames, selectedFrame, updateDraft, graphStats }) {
  const primaryPath = draft.paths.find((path) => path.pathId === draft.primaryReviewPathId) || draft.paths[0] || null;
  const isLibrary = draft.isLibraryGraph === true;
  const runtimeMode = normalizeRuntimeMode(draft.activeRuntimeMode);
  const aiRuntimeSpec = normalizeAiRuntimeSpecDraft(draft.aiRuntimeSpec, selectedFrame, { preserveText: true });

  const setRuntimeMode = (nextMode) => {
    updateDraft((prev) => ({
      ...prev,
      activeRuntimeMode: normalizeRuntimeMode(nextMode),
      aiRuntimeSpec:
        normalizeRuntimeMode(nextMode) === "ai_runtime_spec" && !hasAiRuntimeSpecContent(prev.aiRuntimeSpec)
          ? createDefaultAiRuntimeSpec(selectedFrame)
          : normalizeAiRuntimeSpecDraft(prev.aiRuntimeSpec, selectedFrame, { preserveText: true })
    }));
  };

  const setAiRuntimeSpecList = (field, value) => {
    updateDraft((prev) => ({
      ...prev,
      aiRuntimeSpec: {
        ...normalizeAiRuntimeSpecDraft(prev.aiRuntimeSpec, selectedFrame, { preserveText: true }),
        [field]: normalizeRuntimeSpecList(value, { trim: false })
      }
    }));
  };

  const setAiRuntimeResponseStyle = (field, value) => {
    updateDraft((prev) => ({
      ...prev,
      aiRuntimeSpec: {
        ...normalizeAiRuntimeSpecDraft(prev.aiRuntimeSpec, selectedFrame, { preserveText: true }),
        responseStyle: {
          ...normalizeAiRuntimeSpecDraft(prev.aiRuntimeSpec, selectedFrame, { preserveText: true }).responseStyle,
          [field]: value
        }
      }
    }));
  };

  return (
    <section className="solution-graph-dashboard">
      <div className="solution-graph-summary-grid">
        {isLibrary && (
          <div className="solution-graph-summary-card solution-graph-library-badge-card">
            <span className="solution-graph-library-badge">Library Graph</span>
          </div>
        )}
        <div className="solution-graph-summary-card">
          <span className="solution-graph-summary-label">Status</span>
          <strong>{draft.status || "draft"}</strong>
        </div>
        <div className="solution-graph-summary-card">
          <span className="solution-graph-summary-label">Runtime</span>
          <strong>{runtimeMode === "ai_runtime_spec" ? "AI Spec" : "Graph"}</strong>
        </div>
        <div className="solution-graph-summary-card">
          <span className="solution-graph-summary-label">Nodes</span>
          <strong>{graphStats.nodeCount}</strong>
        </div>
        <div className="solution-graph-summary-card">
          <span className="solution-graph-summary-label">Paths</span>
          <strong>{graphStats.pathCount}</strong>
        </div>
        <div className="solution-graph-summary-card">
          <span className="solution-graph-summary-label">Goals</span>
          <strong>{graphStats.goalCount || 0}</strong>
        </div>
        <div className="solution-graph-summary-card">
          <span className="solution-graph-summary-label">Key Steps</span>
          <strong>{graphStats.keyStepCount}</strong>
        </div>
      </div>

      <div className="solution-graph-dashboard-grid">
        <div className="solution-graph-panel">
          <div className="solution-graph-panel-header">
            <div>
              <h3>Graph Dashboard</h3>
              <div className="solution-graph-help-text">
                {isLibrary
                  ? "This is a library graph — it has no frame and is intended to be used as a sub-graph inside other solution graphs."
                  : "Set the frame context and describe what this graph is teaching before you work inside the canvas."}
              </div>
            </div>
            <span className="solution-graph-status-pill">{draft.status || "draft"}</span>
          </div>

          <div className="solution-graph-dashboard-fields">
            {isLibrary ? (
              <>
                <label className="admin-field">
                  <span>Library Frame</span>
                  <select
                    value={draft.frameId || ""}
                    onChange={(event) => {
                      const nextFrameId = event.target.value;
                      const nextFrame = frames.find((f) => String(f._id) === String(nextFrameId));
                      updateDraft((prev) => ({
                        ...prev,
                        frameId: nextFrameId,
                        chapterId: nextFrame?.chapterId || prev.chapterId,
                        difficulty: nextFrame?.difficulty || prev.difficulty,
                        title: !prev.title ? (nextFrame?.title || "") : prev.title,
                        aiRuntimeSpec: reframeAiRuntimeSpec(prev.aiRuntimeSpec, nextFrame)
                      }));
                    }}
                  >
                    <option value="">Select library frame…</option>
                    {frames.filter((f) => f.isLibraryFrame).map((f) => (
                      <option key={f._id} value={f._id}>{f.title}</option>
                    ))}
                  </select>
                </label>
                {selectedFrame?.isLibraryFrame && (
                  <div className="library-frame-contract">
                    {normalizeContractDisplay(selectedFrame.variablesSchema?.inputs).length > 0 && (
                      <div>
                        <span className="library-contract-label">Inputs:</span>
                        {normalizeContractDisplay(selectedFrame.variablesSchema?.inputs).map(({ name, description }) => (
                          <span key={name} className="library-contract-var">
                            <code>{name}</code>{description ? ` — ${description}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                    {normalizeContractDisplay(selectedFrame.variablesSchema?.outputs).length > 0 && (
                      <div>
                        <span className="library-contract-label">Outputs:</span>
                        {normalizeContractDisplay(selectedFrame.variablesSchema?.outputs).map(({ name, description }) => (
                          <span key={name} className="library-contract-var">
                            <code>{name}</code>{description ? ` — ${description}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <label className="admin-field">
                <span>Frame</span>
                <select
                  value={draft.frameId || ""}
                  onChange={(event) => {
                    const nextFrameId = event.target.value;
                    const nextFrame = frames.find((frame) => String(frame._id) === String(nextFrameId));
                    updateDraft((prev) => ({
                      ...prev,
                      frameId: nextFrameId,
                      title:
                        !prev.title || prev.title === `${selectedFrame?.title || ""} Solution Graph`
                          ? `${nextFrame?.title || ""} Solution Graph`.trim()
                          : prev.title,
                        aiRuntimeSpec: reframeAiRuntimeSpec(prev.aiRuntimeSpec, nextFrame)
                    }));
                  }}
                >
                  <option value="">Select frame</option>
                  {frames.map((frame) => (
                    <option key={frame._id} value={frame._id}>
                      {frame.title}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="admin-field">
              <span>Graph Title</span>
              <input
                value={draft.title || ""}
                onChange={(event) => updateDraft((prev) => ({ ...prev, title: event.target.value }))}
                placeholder="Solution graph title"
              />
            </label>

            <label className="admin-field solution-graph-field-full">
              <span>Description</span>
              <textarea
                value={draft.description || ""}
                onChange={(event) => updateDraft((prev) => ({ ...prev, description: event.target.value }))}
                rows={4}
                placeholder="What does this graph cover?"
              />
            </label>
            <div className="solution-runtime-panel solution-graph-field-full">
              <div className="solution-runtime-heading">
                <span>Active Runtime Flow</span>
                <small>Choose what the student hint, error, and analyze APIs use for this frame.</small>
              </div>
              <div className="solution-runtime-mode-grid">
                {RUNTIME_MODE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`solution-runtime-mode-card${runtimeMode === option.value ? " selected" : ""}`}
                    onClick={() => setRuntimeMode(option.value)}
                  >
                    <strong>{option.label}</strong>
                    <span>{option.summary}</span>
                  </button>
                ))}
              </div>

              {runtimeMode === "ai_runtime_spec" && (
                <div className="solution-runtime-spec-editor">
                  {AI_RUNTIME_SPEC_FIELDS.map((field) => (
                    <label key={field.key} className="admin-field">
                      <span>{field.label}</span>
                      <textarea
                        value={runtimeSpecListToText(aiRuntimeSpec[field.key])}
                        onChange={(event) => setAiRuntimeSpecList(field.key, event.target.value)}
                        rows={3}
                        placeholder={field.placeholder}
                      />
                      <div className="solution-graph-help-text">One guideline per line.</div>
                    </label>
                  ))}
                  <div className="solution-runtime-style-row">
                    <label className="admin-field">
                      <span>Max Words</span>
                      <input
                        type="number"
                        min="25"
                        max="120"
                        value={aiRuntimeSpec.responseStyle.maxWords}
                        onChange={(event) => setAiRuntimeResponseStyle("maxWords", Number(event.target.value) || 55)}
                      />
                    </label>
                    <label className="question-meta">
                      <input
                        type="checkbox"
                        checked={aiRuntimeSpec.responseStyle.noSolutionReveal !== false}
                        onChange={(event) => setAiRuntimeResponseStyle("noSolutionReveal", event.target.checked)}
                      />
                      No solution reveal
                    </label>
                    <label className="question-meta">
                      <input
                        type="checkbox"
                        checked={aiRuntimeSpec.responseStyle.oneNextMoveOnly !== false}
                        onChange={(event) => setAiRuntimeResponseStyle("oneNextMoveOnly", event.target.checked)}
                      />
                      One next move only
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="solution-graph-panel">
          <div className="solution-graph-panel-header">
            <div>
              <h3>Context And Validation</h3>
              <div className="solution-graph-help-text">
                Review the active path and any structural issues before you publish the graph.
              </div>
            </div>
          </div>

          <div className="solution-graph-dashboard-meta">
            <div className="solution-graph-dashboard-meta-card">
              <span className="solution-graph-summary-label">Primary Review Path</span>
              <strong>{primaryPath?.title || "Not set"}</strong>
            </div>
            <div className="solution-graph-dashboard-meta-card">
              <span className="solution-graph-summary-label">Generation Source</span>
              <strong>{draft.generationTrace?.source || "manual"}</strong>
            </div>
            <div className="solution-graph-dashboard-meta-card">
              <span className="solution-graph-summary-label">Edges</span>
              <strong>{graphStats.edgeCount}</strong>
            </div>
            <div className="solution-graph-dashboard-meta-card">
              <span className="solution-graph-summary-label">Canonical Nodes</span>
              <strong>{graphStats.canonicalNodeCount}</strong>
            </div>
          </div>

          <SolutionGraphValidationStack draft={draft} />
        </div>
      </div>
    </section>
  );
}

function SolutionGraphCanvas({
  draft,
  selectedEntity,
  setSelectedEntity,
  canvasViewportRef,
  canvasRef,
  canvasSize,
  addNode,
  nodeTemplateKey,
  onGenerateDraft,
  generating,
  startDraggingNode,
  suppressNodeClickRef,
  startPanningCanvas,
  suppressCanvasClickRef,
  nodeGoalMap
}) {
  const hasGraphContent = (draft.nodes?.length || 0) > 0;

  if (!hasGraphContent) {
    return (
      <div className="solution-graph-canvas-wrap solution-graph-canvas-empty-wrap">
        <div className="solution-graph-canvas-empty">
          <strong>Canvas is ready</strong>
          <p>
            Generate a draft to populate the flow automatically, or add the first node manually and build from there.
          </p>
          <div className="solution-graph-empty-actions">
            <button className="secondary-button" type="button" onClick={onGenerateDraft} disabled={generating}>
              {generating ? "Generating..." : "Generate Draft"}
            </button>
            <button className="primary-button" type="button" onClick={() => addNode(nodeTemplateKey)}>
              Add First Node
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={canvasViewportRef} className="solution-graph-canvas-wrap">
      <div
        ref={canvasRef}
        className="solution-graph-canvas"
        style={{
          width: canvasSize.width,
          height: canvasSize.height
        }}
        onClick={() => {
          if (suppressCanvasClickRef.current && suppressCanvasClickRef.current > Date.now()) {
            suppressCanvasClickRef.current = null;
            return;
          }
          setSelectedEntity(null);
        }}
      >
        <div className="solution-graph-canvas-pan-surface" onPointerDown={startPanningCanvas} />
        <svg className="solution-graph-edge-layer" width={canvasSize.width} height={canvasSize.height}>
          {draft.edges.map((edge) => {
            const fromNode = draft.nodes.find((node) => node.nodeId === edge.fromNodeId);
            const toNode = draft.nodes.find((node) => node.nodeId === edge.toNodeId);
            if (!fromNode || !toNode) return null;
            const x1 = Number(fromNode.position?.x || 0) + NODE_WIDTH / 2;
            const y1 = Number(fromNode.position?.y || 0) + NODE_HEIGHT / 2;
            const x2 = Number(toNode.position?.x || 0) + NODE_WIDTH / 2;
            const y2 = Number(toNode.position?.y || 0) + NODE_HEIGHT / 2;
            const midX = (x1 + x2) / 2;
            const midY = (y1 + y2) / 2;
            return (
              <g key={edge.edgeId}>
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  className={`solution-graph-edge solution-graph-edge-${edge.kind || "required"}`}
                />
                {edge.label ? (
                  <text x={midX} y={midY - 6} className="solution-graph-edge-label">
                    {edge.label}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>

        {draft.nodes.map((node) => {
          const leadPath = draft.paths.find((path) => (node.pathIds || []).includes(path.pathId));
          const leadColor = node.ui?.color || leadPath?.ui?.color || "#cbd5e1";
          const rawAnchor =
            node.anchor?.latex ||
            node.validator?.params?.anchorLatex ||
            node.validator?.params?.targetExpressionLatex ||
            node.validator?.params?.targetClaimLatex ||
            "";
          const anchorPreview = formatAnchorPreviewForCard(rawAnchor);
          return (
            <button
              key={node.nodeId}
              type="button"
              className={`solution-graph-node-card ${selectedEntity?.type === "node" && selectedEntity?.id === node.nodeId ? "selected" : ""}`}
              style={{
                left: node.position?.x || 0,
                top: node.position?.y || 0,
                borderColor: leadColor
              }}
              onClick={(event) => {
                event.stopPropagation();
                const suppressClick = suppressNodeClickRef.current;
                if (
                  suppressClick &&
                  suppressClick.nodeId === node.nodeId &&
                  suppressClick.expiresAt > Date.now()
                ) {
                  suppressNodeClickRef.current = null;
                  return;
                }
                setSelectedEntity({ type: "node", id: node.nodeId });
              }}
              onPointerDown={(event) => {
                event.stopPropagation();
                startDraggingNode(event, node.nodeId);
              }}
            >
              <div className="solution-graph-node-title-row">
                <strong>{node.title}</strong>
                <span className={`solution-graph-node-kind ${node.kind}`}>{node.kind}</span>
              </div>
              <div className="solution-graph-node-chip-row">
                {node.keyStep ? <span className="solution-graph-node-chip">Key Step</span> : null}
                {node.validator?.type === "subgraph" ? (
                  <span className="solution-graph-node-chip">Subgraph</span>
                ) : null}
                {(nodeGoalMap?.get(node.nodeId) || []).slice(0, 1).map((goal) => (
                  <span key={goal.goalId} className="solution-graph-node-goal-badge" title={goal.title}>
                    {goal.title}
                  </span>
                ))}
                {node.pathIds?.slice(0, 1).map((pathId) => {
                  const path = draft.paths.find((item) => item.pathId === pathId);
                  return (
                    <span key={pathId} className="solution-graph-node-chip muted">
                      {path?.title || "Path"}
                    </span>
                  );
                })}
              </div>
              <div className="solution-graph-node-anchor" title={rawAnchor || anchorPreview}>
                {anchorPreview}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function SolutionGraphInspector({
  draft,
  catalog,
  selectedEntity,
  selectedNode,
  selectedPath,
  selectedEdge,
  setNodeField,
  setNodeNestedField,
  setNodeAnchorStepTag,
  setPathField,
  setPathNestedField,
  setEdgeField,
  setNodeValidator,
  deleteSelectedEntity,
  currentRuleDefinition
}) {
  return (
    <aside className="solution-graph-inspector">
      <div className="solution-graph-panel">
        <div className="solution-graph-panel-header">
          <h3>Inspector</h3>
          {selectedEntity ? (
            <button className="secondary-button danger" type="button" onClick={deleteSelectedEntity}>
              Delete
            </button>
          ) : null}
        </div>

        {!selectedEntity && (
          <div className="solution-graph-empty">Select a path, node, or edge to edit its details.</div>
        )}

        {selectedPath && (
          <div className="solution-graph-form-stack">
            <label className="admin-field">
              <span>Path Title</span>
              <input
                value={selectedPath.title || ""}
                onChange={(event) => setPathField(selectedPath.pathId, "title", event.target.value)}
              />
            </label>
            <label className="admin-field">
              <span>Description</span>
              <textarea
                value={selectedPath.description || ""}
                onChange={(event) => setPathField(selectedPath.pathId, "description", event.target.value)}
                rows={3}
              />
            </label>
            <label className="admin-field">
              <span>Color</span>
              <input
                type="color"
                value={selectedPath.ui?.color || "#2563eb"}
                onChange={(event) => setPathNestedField(selectedPath.pathId, "ui", "color", event.target.value)}
              />
            </label>
            <label className="question-meta">
              <input
                type="checkbox"
                checked={selectedPath.isPrimaryReviewPath === true}
                onChange={(event) => setPathField(selectedPath.pathId, "isPrimaryReviewPath", event.target.checked)}
              />
              Primary review path
            </label>
          </div>
        )}

        {selectedEdge && (
          <div className="solution-graph-form-stack">
            <label className="admin-field">
              <span>Label</span>
              <input
                value={selectedEdge.label || ""}
                onChange={(event) => setEdgeField(selectedEdge.edgeId, "label", event.target.value)}
                placeholder="Optional edge label"
              />
            </label>
            <label className="admin-field">
              <span>Kind</span>
              <select
                value={selectedEdge.kind || "required"}
                onChange={(event) => setEdgeField(selectedEdge.edgeId, "kind", event.target.value)}
              >
                {EDGE_KIND_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-field">
              <span>From Node</span>
              <select
                value={selectedEdge.fromNodeId || ""}
                onChange={(event) => setEdgeField(selectedEdge.edgeId, "fromNodeId", event.target.value)}
              >
                <option value="">Select node</option>
                {draft.nodes.map((node) => (
                  <option key={node.nodeId} value={node.nodeId}>
                    {node.title}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-field">
              <span>To Node</span>
              <select
                value={selectedEdge.toNodeId || ""}
                onChange={(event) => setEdgeField(selectedEdge.edgeId, "toNodeId", event.target.value)}
              >
                <option value="">Select node</option>
                {draft.nodes.map((node) => (
                  <option key={node.nodeId} value={node.nodeId}>
                    {node.title}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {selectedNode && (
          <div className="solution-graph-form-stack">
            <label className="admin-field">
              <span>Node Title</span>
              <input
                value={selectedNode.title || ""}
                onChange={(event) => setNodeField(selectedNode.nodeId, "title", event.target.value)}
              />
            </label>
            <label className="admin-field">
              <span>Description</span>
              <textarea
                value={selectedNode.description || ""}
                onChange={(event) => setNodeField(selectedNode.nodeId, "description", event.target.value)}
                rows={3}
              />
            </label>

            <label className="admin-field">
              <span>Condition <span className="solution-graph-help-text">math.js — node is auto-skipped when this evaluates to false</span></span>
              <input
                value={selectedNode.condition || ""}
                onChange={(event) => setNodeField(selectedNode.nodeId, "condition", event.target.value)}
                placeholder="e.g. c == 0  or  a > 0"
              />
            </label>

            {selectedNode.kind === "assignment" && (
              <div className="admin-field">
                <span>Assigns <span className="solution-graph-help-text">inject computed variables into context (no student step)</span></span>
                <AssignsEditor
                  assigns={selectedNode.assigns || {}}
                  onChange={(next) => setNodeField(selectedNode.nodeId, "assigns", next)}
                />
              </div>
            )}

            <div className="solution-graph-two-column">
              <label className="admin-field">
                <span>Node Kind</span>
                <select
                  value={selectedNode.kind || "canonical"}
                  onChange={(event) => setNodeValidator(selectedNode, event.target.value)}
                >
                  <option value="canonical">Canonical</option>
                  <option value="deterministic">Deterministic</option>
                  <option value="assignment">Assignment</option>
                </select>
              </label>

              <label className="admin-field">
                <span>Review Order</span>
                <input
                  type="number"
                  value={selectedNode.reviewOrder ?? ""}
                  onChange={(event) =>
                    setNodeField(
                      selectedNode.nodeId,
                      "reviewOrder",
                      event.target.value === "" ? null : Number(event.target.value)
                    )
                  }
                />
              </label>
            </div>

            <div className="solution-graph-two-column">
              <label className="question-meta">
                <input
                  type="checkbox"
                  checked={selectedNode.keyStep === true}
                  onChange={(event) => setNodeField(selectedNode.nodeId, "keyStep", event.target.checked)}
                />
                Key step
              </label>
              <label className="question-meta">
                <input
                  type="checkbox"
                  checked={selectedNode.anchor?.keyStep === true}
                  onChange={(event) => setNodeNestedField(selectedNode.nodeId, "anchor", "keyStep", event.target.checked)}
                />
                Anchor key step
              </label>
            </div>

            <label className="question-meta">
              <input
                type="checkbox"
                checked={selectedNode.requiredForProgress !== false}
                onChange={(event) => setNodeField(selectedNode.nodeId, "requiredForProgress", event.target.checked)}
              />
              Required for progress
            </label>

            <label className="admin-field">
              <span>Concept Tags</span>
              <input
                value={(selectedNode.conceptTags || []).join(", ")}
                onChange={(event) =>
                  setNodeField(
                    selectedNode.nodeId,
                    "conceptTags",
                    event.target.value
                      .split(",")
                      .map((item) => item.trim())
                      .filter(Boolean)
                  )
                }
                placeholder="e.g. sector_area, inverse_trig_relation"
              />
            </label>

            <div className="solution-graph-path-membership">
              <span className="solution-graph-field-label">Path Membership</span>
              {draft.paths.map((path) => (
                <label key={path.pathId} className="question-meta">
                  <input
                    type="checkbox"
                    checked={(selectedNode.pathIds || []).includes(path.pathId)}
                    onChange={(event) => {
                      const current = new Set(selectedNode.pathIds || []);
                      if (event.target.checked) current.add(path.pathId);
                      else current.delete(path.pathId);
                      setNodeField(selectedNode.nodeId, "pathIds", Array.from(current));
                    }}
                  />
                  {path.title}
                </label>
              ))}
            </div>

            <label className="admin-field">
              <span>Anchor LaTeX</span>
              <textarea
                value={selectedNode.anchor?.latex || ""}
                onChange={(event) => setNodeNestedField(selectedNode.nodeId, "anchor", "latex", event.target.value)}
                rows={4}
                placeholder="Canonical anchor or theorem claim"
              />
            </label>

            <label className="admin-field">
              <span>Anchor Step Tag</span>
              <select
                value={selectedNode.anchor?.stepTag || "other"}
                onChange={(event) => setNodeAnchorStepTag(selectedNode.nodeId, event.target.value)}
              >
                {STEP_TAG_OPTIONS.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </label>

            <label className="admin-field">
              <span>Hint Seed</span>
              <textarea
                value={selectedNode.hint?.seed || ""}
                onChange={(event) => setNodeNestedField(selectedNode.nodeId, "hint", "seed", event.target.value)}
                rows={3}
                placeholder="Short hint seed for this node"
              />
            </label>

            {selectedNode.kind === "assignment" && (
              <div className="solution-graph-help-text" style={{ padding: "8px 0", fontStyle: "italic" }}>
                Assignment nodes are auto-satisfied — no validator or student step required.
                Use the Assigns editor above to compute variables from context.
              </div>
            )}

            {selectedNode.kind !== "assignment" && <div className="solution-graph-validator-block">
              <h4>Validator</h4>
              <label className="admin-field">
                <span>Validator Type</span>
                <select
                  value={selectedNode.validator?.type || ""}
                  onChange={(event) => {
                    const nextType = event.target.value;
                    const nextGroup = getValidatorGroupsForKind(catalog, selectedNode.kind).find((group) => group.type === nextType);
                    const nextRule = nextGroup?.rules?.[0] || null;
                    const params = {};
                    (nextRule?.params || []).forEach((paramDef) => {
                      params[paramDef.key] = getDefaultParamValue(paramDef);
                    });
                    setNodeField(selectedNode.nodeId, "validator", {
                      type: nextType,
                      ruleId: nextRule?.ruleId || "",
                      params
                    });
                    if (nextType) {
                      setNodeField(
                        selectedNode.nodeId,
                        "requiredForProgress",
                        isSupportingDeterministicRule(nextType, nextRule?.ruleId) ? false : selectedNode.requiredForProgress !== false
                      );
                      if (isSupportingDeterministicRule(nextType, nextRule?.ruleId)) {
                        setNodeAnchorStepTag(selectedNode.nodeId, "assumption");
                      }
                    }
                  }}
                >
                  <option value="">Select validator type</option>
                  {getValidatorGroupsForKind(catalog, selectedNode.kind).map((group) => (
                    <option key={group.type} value={group.type}>
                      {group.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="admin-field">
                <span>Rule</span>
                <select
                  value={selectedNode.validator?.ruleId || ""}
                  onChange={(event) => {
                    const { rule } = findRuleDefinition(catalog, selectedNode.validator?.type, event.target.value);
                    const params = {};
                    (rule?.params || []).forEach((paramDef) => {
                      params[paramDef.key] = getDefaultParamValue(paramDef);
                    });
                    setNodeField(selectedNode.nodeId, "validator", {
                      type: selectedNode.validator?.type || "",
                      ruleId: event.target.value,
                      params
                    });
                    setNodeField(
                      selectedNode.nodeId,
                      "requiredForProgress",
                      isSupportingDeterministicRule(selectedNode.validator?.type, event.target.value)
                        ? false
                        : selectedNode.requiredForProgress !== false
                    );
                    if (isSupportingDeterministicRule(selectedNode.validator?.type, event.target.value)) {
                      setNodeAnchorStepTag(selectedNode.nodeId, "assumption");
                    }
                  }}
                >
                  <option value="">Select rule</option>
                  {(findRuleDefinition(catalog, selectedNode.validator?.type, selectedNode.validator?.ruleId).group?.rules || []).map((rule) => (
                    <option key={rule.ruleId} value={rule.ruleId}>
                      {rule.label}
                    </option>
                  ))}
                </select>
              </label>

              {currentRuleDefinition.rule ? (
                <div className="solution-graph-validator-params">
                  {currentRuleDefinition.rule.params
                    .filter((paramDef) => !shouldHideValidatorParam(selectedNode, paramDef))
                    .map((paramDef) => (
                    <label key={paramDef.key} className="admin-field">
                      <span>{paramDef.label}</span>
                      {paramDef.input === "boolean" ? (
                        <label className="question-meta">
                          <input
                            type="checkbox"
                            checked={selectedNode.validator?.params?.[paramDef.key] === true}
                            onChange={(event) =>
                              setNodeField(selectedNode.nodeId, "validator", {
                                ...selectedNode.validator,
                                params: {
                                  ...(selectedNode.validator?.params || {}),
                                  [paramDef.key]: event.target.checked
                                }
                              })
                            }
                          />
                          Enabled
                        </label>
                      ) : paramDef.input === "select" ? (
                        <select
                          value={selectedNode.validator?.params?.[paramDef.key] || ""}
                          onChange={(event) =>
                            setNodeField(selectedNode.nodeId, "validator", {
                              ...selectedNode.validator,
                              params: {
                                ...(selectedNode.validator?.params || {}),
                                [paramDef.key]: event.target.value
                              }
                            })
                          }
                        >
                          <option value="">Select</option>
                          {(paramDef.options || []).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : paramDef.input === "text" ? (
                        <input
                          type="text"
                          value={selectedNode.validator?.params?.[paramDef.key] || ""}
                          onChange={(event) =>
                            setNodeField(selectedNode.nodeId, "validator", {
                              ...selectedNode.validator,
                              params: {
                                ...(selectedNode.validator?.params || {}),
                                [paramDef.key]: event.target.value
                              }
                            })
                          }
                          placeholder={paramDef.helpText || ""}
                        />
                      ) : (
                        <textarea
                          rows={paramDef.input === "textarea" ? 4 : 2}
                          value={selectedNode.validator?.params?.[paramDef.key] || ""}
                          onChange={(event) =>
                            setNodeField(selectedNode.nodeId, "validator", {
                              ...selectedNode.validator,
                              params: {
                                ...(selectedNode.validator?.params || {}),
                                [paramDef.key]: event.target.value
                              }
                            })
                          }
                          placeholder={paramDef.helpText || ""}
                        />
                      )}
                      {paramDef.helpText ? <div className="solution-graph-help-text">{paramDef.helpText}</div> : null}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="solution-graph-empty">Choose a validator type and rule for this node.</div>
              )}
            </div>}
          </div>
        )}
      </div>
    </aside>
  );
}

function SolutionGraphStructureDrawer({
  draft,
  selectedEntity,
  selectedPath,
  selectedEdge,
  selectedGoal,
  setSelectedEntity,
  setPathField,
  setPathNestedField,
  setEdgeField,
  setGoalField,
  addPath,
  addEdge,
  addGoal,
  deleteSelectedEntity,
  closeDrawer
}) {
  return (
    <aside className="solution-graph-drawer">
      <div className="solution-graph-drawer-header">
        <div className="solution-graph-drawer-title">
          <h3>Paths &amp; Edges</h3>
          <div className="solution-graph-help-text">Manage path sequencing, edge dependencies, and structural links.</div>
        </div>
        <div className="solution-graph-drawer-actions">
          {(selectedPath || selectedEdge) ? (
            <button className="secondary-button danger" type="button" onClick={deleteSelectedEntity}>
              Delete
            </button>
          ) : null}
          <button className="secondary-button" type="button" onClick={closeDrawer}>
            Close
          </button>
        </div>
      </div>

      <div className="solution-graph-drawer-body">

        {/* ── Goals ─────────────────────────────────────────────────────── */}
        <div className="solution-graph-panel">
          <div className="solution-graph-panel-header">
            <h3>Goals</h3>
            <button className="secondary-button" type="button" onClick={addGoal}>
              Add Goal
            </button>
          </div>
          <div className="solution-graph-help-text" style={{ padding: "0 0 8px" }}>
            Goals group nodes into named work units. A path can use a goal sequence instead of individual nodes.
          </div>
          <div className="solution-graph-entity-list">
            {(draft.goals || []).map((goal, idx) => (
              <button
                key={goal.goalId}
                type="button"
                className={`solution-graph-goal-chip ${selectedEntity?.type === "goal" && selectedEntity?.id === goal.goalId ? "selected" : ""}`}
                onClick={() => setSelectedEntity({ type: "goal", id: goal.goalId })}
              >
                <span className={`solution-graph-goal-dot${goal.keyGoal ? " key" : ""}`} style={{ background: GOAL_COLORS[idx % GOAL_COLORS.length] }} />
                <span>{goal.title}</span>
                <span style={{ color: "#94a3b8", fontSize: 11 }}>{goal.nodeIds.length} node{goal.nodeIds.length !== 1 ? "s" : ""}</span>
              </button>
            ))}
            {!(draft.goals || []).length && <div className="solution-graph-empty">No goals yet. Add a goal to enable goal-sequence paths.</div>}
          </div>
        </div>

        {/* ── Goal Editor ────────────────────────────────────────────────── */}
        <div className="solution-graph-panel">
          <div className="solution-graph-panel-header">
            <h3>Goal Editor</h3>
          </div>
          {selectedGoal ? (
            <div className="solution-graph-form-stack">
              <label className="admin-field">
                <span>Goal Title</span>
                <input
                  value={selectedGoal.title || ""}
                  onChange={(event) => setGoalField(selectedGoal.goalId, "title", event.target.value)}
                />
              </label>
              <label className="question-meta">
                <input
                  type="checkbox"
                  checked={selectedGoal.keyGoal === true}
                  onChange={(event) => setGoalField(selectedGoal.goalId, "keyGoal", event.target.checked)}
                />
                Key goal (bonus scoring weight)
              </label>
              <div className="solution-graph-path-membership">
                <span className="solution-graph-field-label">Prerequisite Goals</span>
                {(draft.goals || []).filter(g => g.goalId !== selectedGoal.goalId).map((g) => (
                  <label key={g.goalId} className="question-meta">
                    <input
                      type="checkbox"
                      checked={(selectedGoal.requiresGoals || []).includes(g.goalId)}
                      onChange={(event) => {
                        const current = new Set(selectedGoal.requiresGoals || []);
                        if (event.target.checked) current.add(g.goalId);
                        else current.delete(g.goalId);
                        setGoalField(selectedGoal.goalId, "requiresGoals", Array.from(current));
                      }}
                    />
                    {g.title}
                  </label>
                ))}
                {(draft.goals || []).filter(g => g.goalId !== selectedGoal.goalId).length === 0 && (
                  <div className="solution-graph-empty">Add more goals to declare prerequisites.</div>
                )}
              </div>
              <div className="solution-graph-path-membership">
                <span className="solution-graph-field-label">Nodes in this goal</span>
                {draft.nodes.map((node) => (
                  <label key={node.nodeId} className="question-meta">
                    <input
                      type="checkbox"
                      checked={(selectedGoal.nodeIds || []).includes(node.nodeId)}
                      onChange={(event) => {
                        const current = new Set(selectedGoal.nodeIds || []);
                        if (event.target.checked) current.add(node.nodeId);
                        else current.delete(node.nodeId);
                        setGoalField(selectedGoal.goalId, "nodeIds", Array.from(current));
                      }}
                    />
                    {node.title}
                  </label>
                ))}
                {!draft.nodes.length && <div className="solution-graph-empty">Add nodes to the graph first.</div>}
              </div>
            </div>
          ) : (
            <div className="solution-graph-empty">Select a goal from the list above to edit it.</div>
          )}
        </div>

        {/* ── Paths ──────────────────────────────────────────────────────── */}
        <div className="solution-graph-panel">
          <div className="solution-graph-panel-header">
            <h3>Paths</h3>
            <button className="secondary-button" type="button" onClick={addPath}>
              Add Path
            </button>
          </div>
          <div className="solution-graph-entity-list">
            {draft.paths.map((path) => (
              <button
                key={path.pathId}
                type="button"
                className={`solution-graph-entity-chip ${selectedEntity?.type === "path" && selectedEntity?.id === path.pathId ? "selected" : ""}`}
                onClick={() => setSelectedEntity({ type: "path", id: path.pathId })}
              >
                <span className="solution-graph-color-dot" style={{ background: path.ui?.color || "#94a3b8" }} />
                <span>{path.title}</span>
                {(path.goalSequence || []).length > 0 && (
                  <span style={{ fontSize: 11, color: "#7c3aed", marginLeft: 4 }}>goal-seq</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Path Editor ────────────────────────────────────────────────── */}
        <div className="solution-graph-panel">
          <div className="solution-graph-panel-header">
            <h3>Path Editor</h3>
          </div>
          {selectedPath ? (
            <div className="solution-graph-form-stack">
              <label className="admin-field">
                <span>Path Title</span>
                <input
                  value={selectedPath.title || ""}
                  onChange={(event) => setPathField(selectedPath.pathId, "title", event.target.value)}
                />
              </label>
              <label className="admin-field">
                <span>Description</span>
                <textarea
                  value={selectedPath.description || ""}
                  onChange={(event) => setPathField(selectedPath.pathId, "description", event.target.value)}
                  rows={3}
                />
              </label>
              <label className="admin-field">
                <span>Color</span>
                <input
                  type="color"
                  value={selectedPath.ui?.color || "#2563eb"}
                  onChange={(event) => setPathNestedField(selectedPath.pathId, "ui", "color", event.target.value)}
                />
              </label>
              <label className="question-meta">
                <input
                  type="checkbox"
                  checked={selectedPath.isPrimaryReviewPath === true}
                  onChange={(event) => setPathField(selectedPath.pathId, "isPrimaryReviewPath", event.target.checked)}
                />
                Primary review path
              </label>

              {/* Path mode toggle */}
              <div className="admin-field">
                <span className="solution-graph-field-label">Path Mode</span>
                <div className="solution-graph-path-mode">
                  <button
                    type="button"
                    className={`solution-graph-path-mode-btn${!(selectedPath.goalSequence || []).length ? " active" : ""}`}
                    onClick={() => setPathField(selectedPath.pathId, "goalSequence", [])}
                  >
                    Node Sequence
                  </button>
                  <button
                    type="button"
                    className={`solution-graph-path-mode-btn${(selectedPath.goalSequence || []).length > 0 ? " active" : ""}`}
                    onClick={() => {
                      if (!(selectedPath.goalSequence || []).length && (draft.goals || []).length > 0) {
                        setPathField(selectedPath.pathId, "goalSequence", [draft.goals[0].goalId]);
                      } else if (!(draft.goals || []).length) {
                        // no-op: user needs to create goals first
                      }
                    }}
                  >
                    Goal Sequence
                  </button>
                </div>
                {!(draft.goals || []).length && (
                  <div className="solution-graph-help-text" style={{ marginTop: 4 }}>
                    Create goals above before switching to goal-sequence mode.
                  </div>
                )}
              </div>

              {/* Goal sequence editor */}
              {(selectedPath.goalSequence || []).length > 0 && (
                <div className="admin-field">
                  <span className="solution-graph-field-label">Goal Sequence</span>
                  <div className="solution-graph-goal-sequence-list">
                    {selectedPath.goalSequence.map((goalId, idx) => {
                      const goal = (draft.goals || []).find(g => g.goalId === goalId);
                      return (
                        <div key={goalId} className="solution-graph-goal-sequence-item">
                          <span className="solution-graph-goal-dot" />
                          <span>{goal?.title || goalId}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const next = selectedPath.goalSequence.filter((_, i) => i !== idx);
                              setPathField(selectedPath.pathId, "goalSequence", next);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {/* Add goal to sequence */}
                  {(draft.goals || []).filter(g => !selectedPath.goalSequence.includes(g.goalId)).length > 0 && (
                    <select
                      style={{ marginTop: 8, fontSize: 12, padding: "4px 8px", width: "100%", borderRadius: 6, border: "1px dashed #94a3b8" }}
                      value=""
                      onChange={(event) => {
                        if (!event.target.value) return;
                        setPathField(selectedPath.pathId, "goalSequence", [...selectedPath.goalSequence, event.target.value]);
                        event.target.value = "";
                      }}
                    >
                      <option value="">+ Add goal to sequence</option>
                      {(draft.goals || [])
                        .filter(g => !selectedPath.goalSequence.includes(g.goalId))
                        .map(g => (
                          <option key={g.goalId} value={g.goalId}>{g.title}</option>
                        ))
                      }
                    </select>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="solution-graph-empty">Select a path from the list to edit its details.</div>
          )}
        </div>

        <div className="solution-graph-panel">
          <div className="solution-graph-panel-header">
            <h3>Edges</h3>
            <button className="secondary-button" type="button" onClick={addEdge}>
              Add Edge
            </button>
          </div>
          <div className="solution-graph-entity-list">
            {draft.edges.map((edge) => (
              <button
                key={edge.edgeId}
                type="button"
                className={`solution-graph-entity-chip ${selectedEntity?.type === "edge" && selectedEntity?.id === edge.edgeId ? "selected" : ""}`}
                onClick={() => setSelectedEntity({ type: "edge", id: edge.edgeId })}
              >
                <span>{edge.label || formatEdgeDisplay(edge, draft.nodes)}</span>
              </button>
            ))}
            {!draft.edges.length && <div className="solution-graph-empty">No edges yet.</div>}
          </div>
        </div>

        <div className="solution-graph-panel">
          <div className="solution-graph-panel-header">
            <h3>Edge Editor</h3>
          </div>
          {selectedEdge ? (
            <div className="solution-graph-form-stack">
              <label className="admin-field">
                <span>Label</span>
                <input
                  value={selectedEdge.label || ""}
                  onChange={(event) => setEdgeField(selectedEdge.edgeId, "label", event.target.value)}
                  placeholder="Optional edge label"
                />
              </label>
              <label className="admin-field">
                <span>Kind</span>
                <select
                  value={selectedEdge.kind || "required"}
                  onChange={(event) => setEdgeField(selectedEdge.edgeId, "kind", event.target.value)}
                >
                  {EDGE_KIND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span>From Node</span>
                <select
                  value={selectedEdge.fromNodeId || ""}
                  onChange={(event) => setEdgeField(selectedEdge.edgeId, "fromNodeId", event.target.value)}
                >
                  <option value="">Select node</option>
                  {draft.nodes.map((node) => (
                    <option key={node.nodeId} value={node.nodeId}>
                      {node.title}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span>To Node</span>
                <select
                  value={selectedEdge.toNodeId || ""}
                  onChange={(event) => setEdgeField(selectedEdge.edgeId, "toNodeId", event.target.value)}
                >
                  <option value="">Select node</option>
                  {draft.nodes.map((node) => (
                    <option key={node.nodeId} value={node.nodeId}>
                      {node.title}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : (
            <div className="solution-graph-empty">Select an edge from the list to edit the connection.</div>
          )}
        </div>

        <div className="solution-graph-panel">
          <div className="solution-graph-panel-header">
            <h3>Validation</h3>
          </div>
          <SolutionGraphValidationStack draft={draft} />
        </div>
      </div>
    </aside>
  );
}

function AssignsEditor({ assigns, onChange }) {
  const entries = Object.entries(assigns || {});
  function update(oldKey, field, value) {
    const next = {};
    entries.forEach(([k, v]) => {
      if (k === oldKey) {
        const newKey = field === "key" ? value : k;
        next[newKey] = field === "expr" ? value : v;
      } else {
        next[k] = v;
      }
    });
    onChange(next);
  }
  function add() {
    onChange({ ...assigns, "": "" });
  }
  function remove(key) {
    const next = { ...assigns };
    delete next[key];
    onChange(next);
  }
  return (
    <div className="library-var-list">
      {entries.map(([key, expr], i) => (
        <div key={i} className="library-var-row">
          <input
            className="library-var-name"
            value={key}
            onChange={(e) => update(key, "key", e.target.value)}
            placeholder="varName"
          />
          <span style={{ color: "#6b7280", fontSize: 13, flexShrink: 0 }}>=</span>
          <input
            className="library-var-desc"
            value={expr}
            onChange={(e) => update(key, "expr", e.target.value)}
            placeholder="math.js expression, e.g. right_angle_at == 'C' ? c : a"
            style={{ fontFamily: "monospace", fontSize: 12 }}
          />
          <button type="button" className="danger-small" onClick={() => remove(key)}>×</button>
        </div>
      ))}
      <button type="button" className="secondary-button" onClick={add}>+ Add assignment</button>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function LibraryVarList({ vars, onChange, placeholder }) {
  function update(index, field, value) {
    const next = vars.map((v, i) => (i === index ? { ...v, [field]: value } : v));
    onChange(next);
  }
  function add() {
    onChange([...vars, { name: "", description: "" }]);
  }
  function remove(index) {
    onChange(vars.filter((_, i) => i !== index));
  }
  return (
    <div className="library-var-list">
      {vars.map((v, i) => (
        <div key={i} className="library-var-row">
          <input
            className="library-var-name"
            value={v.name || ""}
            onChange={(e) => update(i, "name", e.target.value)}
            placeholder="name"
          />
          <input
            className="library-var-desc"
            value={v.description || ""}
            onChange={(e) => update(i, "description", e.target.value)}
            placeholder={placeholder || "description"}
          />
          <button type="button" className="danger-small" onClick={() => remove(i)}>×</button>
        </div>
      ))}
      <button type="button" className="secondary-button" onClick={add}>+ Add variable</button>
    </div>
  );
}

function SubgraphSourcePicker({ selectedNode, setNodeField, apiBase, token }) {
  const [mode, setMode] = useState("pick");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  const params = selectedNode?.validator?.params || {};
  const subgraphRef = params.subgraphRef || null;

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `${apiBase}/api/admin/solution-graphs/search?q=${encodeURIComponent(query)}&limit=15`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setResults(Array.isArray(data.graphs) ? data.graphs : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, apiBase, token]);

  async function applySnapshot(g, ref) {
    const snapshot = {
      nodes: Array.isArray(g.nodes) ? g.nodes : [],
      paths: Array.isArray(g.paths) ? g.paths : [],
      edges: Array.isArray(g.edges) ? g.edges : []
    };
    setNodeField(selectedNode.nodeId, "validator", {
      ...selectedNode.validator,
      params: { ...params, subgraphDef: snapshot, subgraphRef: ref }
    });
  }

  async function pickGraph(summary) {
    try {
      const res = await fetch(
        `${apiBase}/api/admin/solution-graphs/${summary._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!data.graph) return;
      await applySnapshot(data.graph, {
        graphVersionId: String(summary._id),
        title: summary.title,
        chapterId: summary.chapterId,
        nodeCount: (data.graph.nodes || []).length,
        snapshotAt: new Date().toISOString()
      });
      setQuery("");
      setResults([]);
    } catch (err) {
      console.error("Failed to load subgraph:", err);
    }
  }

  async function resync() {
    if (!subgraphRef?.graphVersionId) return;
    setSyncLoading(true);
    try {
      const res = await fetch(
        `${apiBase}/api/admin/solution-graphs/${subgraphRef.graphVersionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!data.graph) return;
      await applySnapshot(data.graph, {
        ...subgraphRef,
        nodeCount: (data.graph.nodes || []).length,
        snapshotAt: new Date().toISOString()
      });
    } catch (err) {
      console.error("Re-sync failed:", err);
    } finally {
      setSyncLoading(false);
    }
  }

  function unlink() {
    const newParams = { ...params };
    delete newParams.subgraphRef;
    setNodeField(selectedNode.nodeId, "validator", {
      ...selectedNode.validator,
      params: newParams
    });
  }

  return (
    <div className="solution-graph-subgraph-def-block">
      <div className="solution-graph-help-text">
        Link an existing graph as the sub-graph definition, or edit the raw JSON directly.
      </div>

      <div className="solution-graph-subgraph-mode-tabs">
        <button type="button" className={mode === "pick" ? "active" : ""} onClick={() => setMode("pick")}>Select Graph</button>
        <button type="button" className={mode === "json" ? "active" : ""} onClick={() => setMode("json")}>JSON</button>
      </div>

      {mode === "pick" && (
        <div className="solution-graph-subgraph-picker">
          {subgraphRef ? (
            <div className="solution-graph-subgraph-linked">
              <div className="solution-graph-subgraph-linked-info">
                <strong>{subgraphRef.title}</strong>
                <span className="solution-graph-subgraph-linked-meta">
                  {subgraphRef.nodeCount} nodes · {subgraphRef.chapterId}
                </span>
                <span className="solution-graph-subgraph-linked-date">
                  synced {new Date(subgraphRef.snapshotAt).toLocaleString()}
                </span>
              </div>
              <div className="solution-graph-subgraph-linked-actions">
                <button type="button" onClick={resync} disabled={syncLoading}>
                  {syncLoading ? "Syncing…" : "Re-sync"}
                </button>
                <button type="button" onClick={unlink} className="danger-small">Unlink</button>
              </div>
            </div>
          ) : (
            <div className="solution-graph-help-text" style={{ fontStyle: "italic" }}>
              No graph linked. Search below to select one.
            </div>
          )}

          <div className="solution-graph-subgraph-search">
            <input
              type="text"
              placeholder="Search graphs by title…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {searching && <span className="solution-graph-searching-indicator">searching…</span>}
          </div>

          {results.length > 0 && (
            <div className="solution-graph-subgraph-results">
              {results.map((g) => (
                <button
                  key={String(g._id)}
                  type="button"
                  className={
                    "solution-graph-subgraph-result-item" +
                    (subgraphRef?.graphVersionId === String(g._id) ? " selected" : "")
                  }
                  onClick={() => pickGraph(g)}
                >
                  <span className="solution-graph-subgraph-result-title">
                    {g.title}
                    {g.isLibraryGraph && <span className="solution-graph-library-badge" style={{ marginLeft: 6 }}>Library</span>}
                  </span>
                  <span className="solution-graph-subgraph-result-meta">
                    {g.chapterId} · {g.status} · {g.nodeCount}n {g.pathCount}p
                  </span>
                  {g.isLibraryGraph && (g.inputVariables?.length > 0 || g.outputVariables?.length > 0) && (
                    <span className="solution-graph-subgraph-result-contract">
                      {g.inputVariables?.length > 0 && (
                        <span>in: {g.inputVariables.map((v) => v.name).join(", ")}</span>
                      )}
                      {g.inputVariables?.length > 0 && g.outputVariables?.length > 0 && <span> → </span>}
                      {g.outputVariables?.length > 0 && (
                        <span>out: {g.outputVariables.map((v) => v.name).join(", ")}</span>
                      )}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === "json" && (
        <label className="admin-field">
          <span>Sub-graph Definition (JSON)</span>
          <textarea
            rows={12}
            style={{ fontFamily: "monospace", fontSize: 11 }}
            value={(() => {
              const def = params.subgraphDef;
              if (!def) return "";
              try { return JSON.stringify(def, null, 2); } catch { return ""; }
            })()}
            onChange={(event) => {
              try {
                const parsed = JSON.parse(event.target.value);
                setNodeField(selectedNode.nodeId, "validator", {
                  ...selectedNode.validator,
                  params: { ...params, subgraphDef: parsed }
                });
              } catch {
                // keep raw text until valid JSON
              }
            }}
            placeholder='{ "nodes": [], "paths": [] }'
          />
        </label>
      )}
    </div>
  );
}

function SolutionGraphNodeDrawer({
  draft,
  catalog,
  selectedNode,
  setNodeField,
  setNodeNestedField,
  setNodeAnchorStepTag,
  setNodeValidator,
  deleteSelectedEntity,
  closeDrawer,
  currentRuleDefinition,
  nodeGoalMap,
  apiBase,
  token
}) {
  return (
    <aside className="solution-graph-drawer">
      <div className="solution-graph-drawer-header">
        <div className="solution-graph-drawer-title">
          <h3>Node Inspector</h3>
          <div className="solution-graph-help-text">Edit node details, validator settings, and hint metadata.</div>
        </div>
        <div className="solution-graph-drawer-actions">
          {selectedNode ? (
            <button className="secondary-button danger" type="button" onClick={deleteSelectedEntity}>
              Delete
            </button>
          ) : null}
          <button className="secondary-button" type="button" onClick={closeDrawer}>
            Close
          </button>
        </div>
      </div>

      <div className="solution-graph-drawer-body">
        {!selectedNode ? (
          <div className="solution-graph-panel">
            <div className="solution-graph-empty">Select a node on the canvas to inspect and edit it.</div>
          </div>
        ) : (
          <div className="solution-graph-panel">
            <div className="solution-graph-form-stack">
              <label className="admin-field">
                <span>Node Title</span>
                <input
                  value={selectedNode.title || ""}
                  onChange={(event) => setNodeField(selectedNode.nodeId, "title", event.target.value)}
                />
              </label>
              <label className="admin-field">
                <span>Description</span>
                <textarea
                  value={selectedNode.description || ""}
                  onChange={(event) => setNodeField(selectedNode.nodeId, "description", event.target.value)}
                  rows={3}
                />
              </label>

              <label className="admin-field">
                <span>Condition <span className="solution-graph-help-text">math.js — node auto-skipped when false</span></span>
                <input
                  value={selectedNode.condition || ""}
                  onChange={(event) => setNodeField(selectedNode.nodeId, "condition", event.target.value)}
                  placeholder="e.g. c == 0  or  a > 0"
                />
              </label>

              <div className="solution-graph-two-column">
                <label className="admin-field">
                  <span>Node Kind</span>
                  <select
                    value={selectedNode.kind || "canonical"}
                    onChange={(event) => setNodeValidator(selectedNode, event.target.value)}
                  >
                    <option value="canonical">Canonical</option>
                    <option value="deterministic">Deterministic</option>
                    <option value="assignment">Assignment</option>
                  </select>
                </label>

                <label className="admin-field">
                  <span>Review Order</span>
                  <input
                    type="number"
                    value={selectedNode.reviewOrder ?? ""}
                    onChange={(event) =>
                      setNodeField(
                        selectedNode.nodeId,
                        "reviewOrder",
                        event.target.value === "" ? null : Number(event.target.value)
                      )
                    }
                  />
                </label>
              </div>

              {selectedNode.kind === "assignment" && (
                <div className="admin-field">
                  <span>Assigns <span className="solution-graph-help-text">inject computed variables into context — no student step needed</span></span>
                  <AssignsEditor
                    assigns={selectedNode.assigns || {}}
                    onChange={(next) => setNodeField(selectedNode.nodeId, "assigns", next)}
                  />
                </div>
              )}

              <div className="solution-graph-two-column">
                <label className="question-meta">
                  <input
                    type="checkbox"
                    checked={selectedNode.keyStep === true}
                    onChange={(event) => setNodeField(selectedNode.nodeId, "keyStep", event.target.checked)}
                  />
                  Key step
                </label>
                <label className="question-meta">
                  <input
                    type="checkbox"
                    checked={selectedNode.anchor?.keyStep === true}
                    onChange={(event) =>
                      setNodeNestedField(selectedNode.nodeId, "anchor", "keyStep", event.target.checked)
                    }
                  />
                  Anchor key step
                </label>
              </div>

              <label className="question-meta">
                <input
                  type="checkbox"
                  checked={selectedNode.requiredForProgress !== false}
                  onChange={(event) => setNodeField(selectedNode.nodeId, "requiredForProgress", event.target.checked)}
                />
                Required for progress
              </label>

              <label className="admin-field">
                <span>Concept Tags</span>
                <input
                  value={(selectedNode.conceptTags || []).join(", ")}
                  onChange={(event) =>
                    setNodeField(
                      selectedNode.nodeId,
                      "conceptTags",
                      event.target.value
                        .split(",")
                        .map((item) => item.trim())
                        .filter(Boolean)
                    )
                  }
                  placeholder="e.g. sector_area, inverse_trig_relation"
                />
              </label>

              <div className="solution-graph-path-membership">
                <span className="solution-graph-field-label">Path Membership</span>
                {draft.paths.map((path) => (
                  <label key={path.pathId} className="question-meta">
                    <input
                      type="checkbox"
                      checked={(selectedNode.pathIds || []).includes(path.pathId)}
                      onChange={(event) => {
                        const current = new Set(selectedNode.pathIds || []);
                        if (event.target.checked) current.add(path.pathId);
                        else current.delete(path.pathId);
                        setNodeField(selectedNode.nodeId, "pathIds", Array.from(current));
                      }}
                    />
                    {path.title}
                  </label>
                ))}
              </div>

              {(draft.goals || []).length > 0 && (
                <div className="solution-graph-path-membership">
                  <span className="solution-graph-field-label">Goal Membership</span>
                  {(draft.goals || []).map((goal) => (
                    <label key={goal.goalId} className="question-meta">
                      <input
                        type="checkbox"
                        checked={(goal.nodeIds || []).includes(selectedNode.nodeId)}
                        onChange={(event) => {
                          const current = new Set(goal.nodeIds || []);
                          if (event.target.checked) current.add(selectedNode.nodeId);
                          else current.delete(selectedNode.nodeId);
                          // Update via a parent callback that writes goal fields
                          setNodeField("__goal__" + goal.goalId, "nodeIds", Array.from(current));
                        }}
                      />
                      {goal.title}{goal.keyGoal ? " (key)" : ""}
                    </label>
                  ))}
                </div>
              )}

              <label className="admin-field">
                <span>Anchor LaTeX</span>
                <textarea
                  value={selectedNode.anchor?.latex || ""}
                  onChange={(event) => setNodeNestedField(selectedNode.nodeId, "anchor", "latex", event.target.value)}
                  rows={4}
                  placeholder="Canonical anchor or theorem claim"
                />
              </label>

              <label className="admin-field">
                <span>Anchor Step Tag</span>
                <select
                  value={selectedNode.anchor?.stepTag || "other"}
                  onChange={(event) => setNodeAnchorStepTag(selectedNode.nodeId, event.target.value)}
                >
                  {STEP_TAG_OPTIONS.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </label>

              <label className="admin-field">
                <span>Hint Seed</span>
                <textarea
                  value={selectedNode.hint?.seed || ""}
                  onChange={(event) => setNodeNestedField(selectedNode.nodeId, "hint", "seed", event.target.value)}
                  rows={3}
                  placeholder="Short hint seed for this node"
                />
              </label>

              {selectedNode.kind === "assignment" && (
                <div className="solution-graph-help-text" style={{ padding: "8px 0", fontStyle: "italic" }}>
                  Assignment nodes are auto-satisfied — no validator or student step required.
                </div>
              )}

              {selectedNode.kind !== "assignment" && <div className="solution-graph-validator-block">
                <h4>Validator</h4>
                <label className="admin-field">
                  <span>Validator Type</span>
                  <select
                    value={selectedNode.validator?.type || ""}
                    onChange={(event) => {
                      const nextType = event.target.value;
                      const nextGroup = getValidatorGroupsForKind(catalog, selectedNode.kind).find(
                        (group) => group.type === nextType
                      );
                      const nextRule = nextGroup?.rules?.[0] || null;
                      const params = {};
                      (nextRule?.params || []).forEach((paramDef) => {
                        params[paramDef.key] = getDefaultParamValue(paramDef);
                      });
                      setNodeField(selectedNode.nodeId, "validator", {
                        type: nextType,
                        ruleId: nextRule?.ruleId || "",
                        params
                      });
                      if (nextType) {
                        setNodeField(
                          selectedNode.nodeId,
                          "requiredForProgress",
                          isSupportingDeterministicRule(nextType, nextRule?.ruleId) ? false : selectedNode.requiredForProgress !== false
                        );
                        if (isSupportingDeterministicRule(nextType, nextRule?.ruleId)) {
                          setNodeAnchorStepTag(selectedNode.nodeId, "assumption");
                        }
                      }
                    }}
                  >
                    <option value="">Select validator type</option>
                    {getValidatorGroupsForKind(catalog, selectedNode.kind).map((group) => (
                      <option key={group.type} value={group.type}>
                        {group.label}
                      </option>
                    ))}
                    <option value="subgraph">Subgraph</option>
                  </select>
                </label>

                {selectedNode.validator?.type !== "subgraph" && (
                  <label className="admin-field">
                    <span>Rule</span>
                    <select
                      value={selectedNode.validator?.ruleId || ""}
                      onChange={(event) => {
                        const { rule } = findRuleDefinition(catalog, selectedNode.validator?.type, event.target.value);
                        const params = {};
                        (rule?.params || []).forEach((paramDef) => {
                          params[paramDef.key] = getDefaultParamValue(paramDef);
                        });
                        setNodeField(selectedNode.nodeId, "validator", {
                          type: selectedNode.validator?.type || "",
                          ruleId: event.target.value,
                          params
                        });
                        setNodeField(
                          selectedNode.nodeId,
                          "requiredForProgress",
                          isSupportingDeterministicRule(selectedNode.validator?.type, event.target.value)
                            ? false
                            : selectedNode.requiredForProgress !== false
                        );
                        if (isSupportingDeterministicRule(selectedNode.validator?.type, event.target.value)) {
                          setNodeAnchorStepTag(selectedNode.nodeId, "assumption");
                        }
                      }}
                    >
                      <option value="">Select rule</option>
                      {(
                        findRuleDefinition(catalog, selectedNode.validator?.type, selectedNode.validator?.ruleId).group
                          ?.rules || []
                      ).map((rule) => (
                        <option key={rule.ruleId} value={rule.ruleId}>
                          {rule.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {/* Subgraph-specific: pick from existing graph or edit JSON */}
                {selectedNode.validator?.type === "subgraph" && (
                  <SubgraphSourcePicker
                    selectedNode={selectedNode}
                    setNodeField={setNodeField}
                    apiBase={apiBase}
                    token={token}
                  />
                )}

                {/* Emits editor — available for all node types */}
                <div className="solution-graph-emits-block">
                  <strong style={{ fontSize: 13 }}>Emitted Variables</strong>
                  <div className="solution-graph-help-text">
                    {selectedNode.validator?.type === "subgraph"
                      ? <>Map variable names to nodes inside the sub-graph. Use <code>fromNode</code> to specify which sub-graph node to extract from. Downstream nodes access values via <code>{"{{varName}}"}</code>.</>
                      : <>Extract a value when this node is satisfied and expose it as <code>{"{{varName}}"}</code> downstream. Use <strong>From expression</strong> on deterministic nodes to compute from context variables (e.g. <code>opp / hyp</code>) without needing the student&apos;s step text.</>
                    }
                  </div>
                  {Object.entries(selectedNode.emits || {}).map(([varName, spec]) => (
                    <div key={varName} className="solution-graph-emit-row">
                      <input
                        value={varName}
                        placeholder="varName"
                        onChange={(event) => {
                          const newEmits = { ...(selectedNode.emits || {}) };
                          delete newEmits[varName];
                          if (event.target.value) newEmits[event.target.value] = spec;
                          setNodeField(selectedNode.nodeId, "emits", Object.keys(newEmits).length ? newEmits : null);
                        }}
                      />
                      {selectedNode.validator?.type === "subgraph" && (
                        <input
                          value={typeof spec === "string" ? spec : (spec?.fromNode || "")}
                          placeholder="fromNode (nodeId)"
                          onChange={(event) => {
                            const newSpec = typeof spec === "object" ? { ...spec, fromNode: event.target.value } : { fromNode: event.target.value, extract: "rhs_value" };
                            setNodeField(selectedNode.nodeId, "emits", { ...(selectedNode.emits || {}), [varName]: newSpec });
                          }}
                        />
                      )}
                      <select
                        value={typeof spec === "object" ? (spec?.extract || "rhs_value") : "rhs_value"}
                        onChange={(event) => {
                          const newSpec = typeof spec === "object" ? { ...spec, extract: event.target.value } : { extract: event.target.value };
                          setNodeField(selectedNode.nodeId, "emits", { ...(selectedNode.emits || {}), [varName]: newSpec });
                        }}
                      >
                        {EMIT_EXTRACT_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      {typeof spec === "object" && spec?.extract === "fromExpression" && (
                        <input
                          style={{ fontFamily: "monospace", fontSize: 12, flex: 2 }}
                          value={spec?.expression || ""}
                          placeholder="math.js expression, e.g. opp / hyp"
                          onChange={(event) => {
                            const newSpec = { ...spec, expression: event.target.value };
                            setNodeField(selectedNode.nodeId, "emits", { ...(selectedNode.emits || {}), [varName]: newSpec });
                          }}
                        />
                      )}
                      <button
                        type="button"
                        className="emit-delete-btn"
                        onClick={() => {
                          const newEmits = { ...(selectedNode.emits || {}) };
                          delete newEmits[varName];
                          setNodeField(selectedNode.nodeId, "emits", Object.keys(newEmits).length ? newEmits : null);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="solution-graph-add-emit-btn"
                    onClick={() => {
                      const newKey = `var${Object.keys(selectedNode.emits || {}).length + 1}`;
                      setNodeField(selectedNode.nodeId, "emits", {
                        ...(selectedNode.emits || {}),
                        [newKey]: selectedNode.validator?.type === "subgraph"
                          ? { fromNode: "", extract: "rhs_value" }
                          : { extract: "rhs_value" }
                      });
                    }}
                  >
                    + Add emitted variable
                  </button>
                </div>

                {selectedNode.validator?.type !== "subgraph" && currentRuleDefinition.rule ? (
                  <div className="solution-graph-validator-params">
                    {currentRuleDefinition.rule.params
                      .filter((paramDef) => !shouldHideValidatorParam(selectedNode, paramDef))
                      .map((paramDef) => (
                      <label key={paramDef.key} className="admin-field">
                        <span>{paramDef.label}</span>
                        {paramDef.input === "boolean" ? (
                          <label className="question-meta">
                            <input
                              type="checkbox"
                              checked={selectedNode.validator?.params?.[paramDef.key] === true}
                              onChange={(event) =>
                                setNodeField(selectedNode.nodeId, "validator", {
                                  ...selectedNode.validator,
                                  params: {
                                    ...(selectedNode.validator?.params || {}),
                                    [paramDef.key]: event.target.checked
                                  }
                                })
                              }
                            />
                            Enabled
                          </label>
                        ) : paramDef.input === "select" ? (
                          <select
                            value={selectedNode.validator?.params?.[paramDef.key] || ""}
                            onChange={(event) =>
                              setNodeField(selectedNode.nodeId, "validator", {
                                ...selectedNode.validator,
                                params: {
                                  ...(selectedNode.validator?.params || {}),
                                  [paramDef.key]: event.target.value
                                }
                              })
                            }
                          >
                            <option value="">Select</option>
                            {(paramDef.options || []).map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : paramDef.input === "text" ? (
                          <input
                            type="text"
                            value={selectedNode.validator?.params?.[paramDef.key] || ""}
                            onChange={(event) =>
                              setNodeField(selectedNode.nodeId, "validator", {
                                ...selectedNode.validator,
                                params: {
                                  ...(selectedNode.validator?.params || {}),
                                  [paramDef.key]: event.target.value
                                }
                              })
                            }
                            placeholder={paramDef.helpText || ""}
                          />
                        ) : (
                          <textarea
                            rows={paramDef.input === "textarea" ? 4 : 2}
                            value={selectedNode.validator?.params?.[paramDef.key] || ""}
                            onChange={(event) =>
                              setNodeField(selectedNode.nodeId, "validator", {
                                ...selectedNode.validator,
                                params: {
                                  ...(selectedNode.validator?.params || {}),
                                  [paramDef.key]: event.target.value
                                }
                              })
                            }
                            placeholder={paramDef.helpText || ""}
                          />
                        )}
                        {paramDef.helpText ? <div className="solution-graph-help-text">{paramDef.helpText}</div> : null}
                      </label>
                    ))}
                  </div>
                ) : selectedNode.validator?.type !== "subgraph" ? (
                  <div className="solution-graph-empty">Choose a validator type and rule for this node.</div>
                ) : null}
              </div>}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

function SolutionGraphWorkspace({
  draft,
  selectedEntity,
  selectedNode,
  selectedPath,
  selectedEdge,
  selectedGoal,
  setSelectedEntity,
  canvasViewportRef,
  canvasRef,
  canvasSize,
  nodeTemplateOptions,
  nodeTemplateKey,
  setNodeTemplateKey,
  addNode,
  duplicateSelectedNode,
  startDraggingNode,
  suppressNodeClickRef,
  startPanningCanvas,
  suppressCanvasClickRef,
  openDrawer,
  setOpenDrawer,
  generateDraft,
  generating,
  autoLayoutNodes,
  validateGraph,
  validating,
  catalog,
  setNodeField,
  setNodeNestedField,
  setNodeAnchorStepTag,
  setPathField,
  setPathNestedField,
  setEdgeField,
  setGoalField,
  setNodeValidator,
  addPath,
  addEdge,
  addGoal,
  deleteSelectedEntity,
  currentRuleDefinition,
  nodeGoalMap,
  apiBase,
  token
}) {
  return (
    <section className="solution-graph-workspace">
      <div className="solution-graph-workspace-header">
        <div className="solution-graph-workspace-title">
          <h3>Canvas Workspace</h3>
          <div className="solution-graph-help-text">
            Arrange the graph visually here. Open the right-side drawers only when you need detailed editing.
          </div>
        </div>

        <div className="solution-graph-workspace-actions">
          <button
            className={`secondary-button solution-graph-drawer-toggle ${openDrawer === "structure" ? "active" : ""}`}
            type="button"
            onClick={() => setOpenDrawer((current) => (current === "structure" ? null : "structure"))}
          >
            Goals &amp; Paths
          </button>
          <button
            className={`secondary-button solution-graph-drawer-toggle ${openDrawer === "node" ? "active" : ""}`}
            type="button"
            onClick={() => setOpenDrawer((current) => (current === "node" ? null : "node"))}
          >
            Node Inspector
          </button>
        </div>
      </div>

      <div className="solution-graph-workspace-toolbar">
        <div className="solution-graph-toolbar-group">
          <button className="secondary-button" type="button" onClick={generateDraft} disabled={generating}>
            {generating ? "Generating..." : "Generate Draft"}
          </button>
          <button className="secondary-button" type="button" onClick={autoLayoutNodes}>
            Auto Layout
          </button>
          <button className="secondary-button" type="button" onClick={validateGraph} disabled={validating}>
            {validating ? "Validating..." : "Validate"}
          </button>
        </div>
        <div className="solution-graph-toolbar-group">
          <select
            className="solution-graph-node-template-select"
            value={nodeTemplateKey}
            onChange={(event) => setNodeTemplateKey(event.target.value)}
          >
            {(nodeTemplateOptions || []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button className="secondary-button" type="button" onClick={() => addNode(nodeTemplateKey)}>
            Add Node
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={duplicateSelectedNode}
            disabled={!selectedNode}
          >
            Duplicate Node
          </button>
        </div>
        <div className="solution-graph-canvas-note">
          Drag cards to arrange the flow. The canvas stays central, and the drawers slide in only when you need them.
        </div>
      </div>

      <div className="solution-graph-workspace-body">
        <SolutionGraphCanvas
          draft={draft}
          selectedEntity={selectedEntity}
          setSelectedEntity={setSelectedEntity}
          canvasViewportRef={canvasViewportRef}
          canvasRef={canvasRef}
          canvasSize={canvasSize}
          addNode={addNode}
          nodeTemplateKey={nodeTemplateKey}
          onGenerateDraft={generateDraft}
          generating={generating}
          startDraggingNode={startDraggingNode}
          suppressNodeClickRef={suppressNodeClickRef}
          startPanningCanvas={startPanningCanvas}
          suppressCanvasClickRef={suppressCanvasClickRef}
          nodeGoalMap={nodeGoalMap}
        />

        {openDrawer === "structure" ? (
          <SolutionGraphStructureDrawer
            draft={draft}
            selectedEntity={selectedEntity}
            selectedPath={selectedPath}
            selectedEdge={selectedEdge}
            selectedGoal={selectedGoal}
            setSelectedEntity={setSelectedEntity}
            setPathField={setPathField}
            setPathNestedField={setPathNestedField}
            setEdgeField={setEdgeField}
            setGoalField={setGoalField}
            addPath={addPath}
            addEdge={addEdge}
            addGoal={addGoal}
            deleteSelectedEntity={deleteSelectedEntity}
            closeDrawer={() => setOpenDrawer(null)}
          />
        ) : null}

        {openDrawer === "node" ? (
          <SolutionGraphNodeDrawer
            draft={draft}
            catalog={catalog}
            selectedNode={selectedNode}
            setNodeField={setNodeField}
            setNodeNestedField={setNodeNestedField}
            setNodeAnchorStepTag={setNodeAnchorStepTag}
            setNodeValidator={setNodeValidator}
            deleteSelectedEntity={deleteSelectedEntity}
            closeDrawer={() => setOpenDrawer(null)}
            currentRuleDefinition={currentRuleDefinition}
            nodeGoalMap={nodeGoalMap}
            apiBase={apiBase}
            token={token}
          />
        ) : null}
      </div>
    </section>
  );
}

function SolutionGraphGenerationModal({
  open,
  closeModal,
  selectedFrame,
  previewContext,
  loadingContext,
  sampleVariablesText,
  setSampleVariablesText,
  parsedSampleVariables,
  promptTemplate,
  setPromptTemplate,
  refreshPreviewContext,
  generateDraft,
  generating,
  runGraphPreview,
  graphPreview,
  graphPreviewLoading,
  previewPathId,
  setPreviewPathId,
  previewGraph
}) {
  if (!open) return null;

  const previewPaths = Array.isArray(previewGraph?.paths) ? previewGraph.paths : [];

  return (
    <div className="solution-graph-modal" role="dialog" aria-modal="true">
      <div className="solution-graph-modal-card">
        <div className="solution-graph-modal-header">
          <div className="solution-graph-modal-title">
            <h3>Generate Solution Graph</h3>
            <div className="solution-graph-help-text">
              Review the input context, edit the prompt if needed, and run a preview before generating the draft.
            </div>
          </div>
          <button className="secondary-button" type="button" onClick={closeModal}>
            Close
          </button>
        </div>

        <div className="solution-graph-modal-body">
          {loadingContext ? (
            <div className="solution-graph-panel">
              <div className="solution-graph-empty">Loading generation context...</div>
            </div>
          ) : (
            <>
              <div className="solution-graph-modal-grid">
                <div className="solution-graph-panel">
                  <div className="solution-graph-panel-header">
                    <h3>Frame Preview</h3>
                  </div>
                  <div className="solution-graph-form-stack">
                    <div className="question-meta">
                      Chapter: {previewContext?.chapterId || selectedFrame?.chapterId || "n/a"} | Difficulty:{" "}
                      {previewContext?.difficulty || selectedFrame?.difficulty || "n/a"}
                    </div>
                    <div className="question-meta">Frame Title: {previewContext?.frameTitle || selectedFrame?.title || "Untitled frame"}</div>
                    {previewContext?.renderedQuestion ? (
                      <BlockMath math={previewContext.renderedQuestion} />
                    ) : (
                      <div className="solution-graph-empty">No rendered question available.</div>
                    )}
                  </div>
                </div>

                <div className="solution-graph-panel">
                  <div className="solution-graph-panel-header">
                    <h3>Variables And Prompt</h3>
                  </div>
                  <div className="solution-graph-form-stack">
                    <label className="admin-field">
                      <span>Input Variables JSON</span>
                      <textarea
                        rows={8}
                        value={sampleVariablesText}
                        onChange={(event) => setSampleVariablesText(event.target.value)}
                        placeholder='{"x1": -8, "y1": -8}'
                      />
                    </label>
                    {!parsedSampleVariables.ok ? (
                      <div className="error-text">Invalid variables JSON: {parsedSampleVariables.error}</div>
                    ) : null}
                    <label className="admin-field">
                      <span>Prompt Template</span>
                      <textarea
                        rows={16}
                        value={promptTemplate}
                        onChange={(event) => setPromptTemplate(event.target.value)}
                      />
                    </label>
                    <div className="solution-graph-help-text">
                      Tokens: <code>{"{{FRAME_TITLE}}"}</code>, <code>{"{{QUESTION_LATEX}}"}</code>,{" "}
                      <code>{"{{VARIABLES_JSON}}"}</code>, <code>{"{{FRAME_SCHEMA_JSON}}"}</code>,{" "}
                      <code>{"{{DIAGRAM_SCHEMA_JSON}}"}</code>, <code>{"{{DIAGRAM_SUMMARY}}"}</code>,{" "}
                      <code>{"{{FIGURE_SPEC_JSON}}"}</code>, <code>{"{{VALIDATOR_CATALOG_JSON}}"}</code>
                    </div>
                  </div>
                </div>
              </div>

              <div className="solution-graph-modal-actions">
                <button
                  className="secondary-button"
                  type="button"
                  onClick={refreshPreviewContext}
                  disabled={loadingContext || !parsedSampleVariables.ok}
                >
                  {loadingContext ? "Refreshing..." : "Refresh Prompt Preview"}
                </button>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={runGraphPreview}
                  disabled={graphPreviewLoading || !parsedSampleVariables.ok}
                >
                  {graphPreviewLoading ? "Previewing..." : "Run Graph Preview"}
                </button>
                <button
                  className="primary-button"
                  type="button"
                  onClick={generateDraft}
                  disabled={generating || !parsedSampleVariables.ok}
                >
                  {generating ? "Generating..." : "Generate Draft"}
                </button>
              </div>

              <details open className="solution-graph-modal-section">
                <summary>Rendered Prompt</summary>
                <pre className="analysis-box">{previewContext?.renderedPrompt || promptTemplate || ""}</pre>
              </details>

              <details className="solution-graph-modal-section">
                <summary>Frame Structure</summary>
                <div className="question-meta">Variables schema / constraints</div>
                <pre className="analysis-box">{JSON.stringify(previewContext?.variablesSchema || null, null, 2)}</pre>
                <div className="question-meta">Frame schema</div>
                <pre className="analysis-box">{JSON.stringify(previewContext?.frameSchema || null, null, 2)}</pre>
              </details>

              <details className="solution-graph-modal-section">
                <summary>Diagram And Figure Context</summary>
                <div className="question-meta">Diagram summary</div>
                <pre className="analysis-box">
                  {JSON.stringify(previewContext?.diagramSummary || null, null, 2)}
                </pre>
                <div className="question-meta">Diagram schema</div>
                <pre className="analysis-box">{JSON.stringify(previewContext?.diagramSchema || null, null, 2)}</pre>
                <div className="question-meta">Figure spec</div>
                <pre className="analysis-box">{JSON.stringify(previewContext?.figureSpec || null, null, 2)}</pre>
              </details>

              <details className="solution-graph-modal-section">
                <summary>Graph Draft Baseline</summary>
                <div className="question-meta">Empty baseline graph</div>
                <pre className="analysis-box">
                  {JSON.stringify(previewContext?.compiledBaselineGraph || null, null, 2)}
                </pre>
                <div className="question-meta">Baseline validation</div>
                <pre className="analysis-box">
                  {JSON.stringify(previewContext?.compiledBaselineValidation || null, null, 2)}
                </pre>
              </details>

              <details open className="solution-graph-modal-section">
                <summary>Solution Run Preview</summary>
                {previewPaths.length > 1 ? (
                  <label className="admin-field solution-graph-modal-inline-field">
                    <span>Preview Path</span>
                    <select value={previewPathId || ""} onChange={(event) => setPreviewPathId(event.target.value)}>
                      <option value="">Primary path</option>
                      {previewPaths.map((path) => (
                        <option key={path.pathId} value={path.pathId}>
                          {path.title}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {graphPreview?.steps?.length ? (
                  <>
                    <div className="question-meta">Variables used</div>
                    <pre className="analysis-box">{JSON.stringify(graphPreview.variables || {}, null, 2)}</pre>
                    <div className="analysis-grid">
                      {graphPreview.steps.map((step, index) => (
                        <div key={`graph-preview-${index}`}>
                          <div className="question-meta">
                            Step {index + 1} | {step.tag} {step.keyStep ? "(key)" : ""}
                          </div>
                          <BlockMath math={step.latex} />
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="solution-graph-empty">
                    Run the preview to see the selected path rendered with the sample variables.
                  </div>
                )}
              </details>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminSolutionGraphEditor({ apiBase, token }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { graphId } = useParams();
  const isCreate = !graphId;

  const [frames, setFrames] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [notice, setNotice] = useState({ type: "", text: "" });
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [nodeTemplateKey, setNodeTemplateKey] = useState("blank_canonical");
  const [openDrawer, setOpenDrawer] = useState(null);
  const [generationModalOpen, setGenerationModalOpen] = useState(false);
  const [generationPreviewLoading, setGenerationPreviewLoading] = useState(false);
  const [generationPreviewContext, setGenerationPreviewContext] = useState(null);
  const [generationPromptTemplate, setGenerationPromptTemplate] = useState("");
  const [sampleVariablesText, setSampleVariablesText] = useState("{}");
  const [graphPreview, setGraphPreview] = useState({ steps: [], variables: {} });
  const [graphPreviewLoading, setGraphPreviewLoading] = useState(false);
  const [previewPathId, setPreviewPathId] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const canvasViewportRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const suppressNodeClickRef = useRef(null);
  const panRef = useRef(null);
  const suppressCanvasClickRef = useRef(null);

  const selectedFrame = useMemo(
    () => frames.find((frame) => String(frame._id) === String(draft?.frameId || "")) || null,
    [frames, draft?.frameId]
  );

  const selectedNode = useMemo(() => {
    if (selectedEntity?.type !== "node" || !draft) return null;
    return draft.nodes.find((node) => node.nodeId === selectedEntity.id) || null;
  }, [draft, selectedEntity]);

  const selectedPath = useMemo(() => {
    if (selectedEntity?.type !== "path" || !draft) return null;
    return draft.paths.find((path) => path.pathId === selectedEntity.id) || null;
  }, [draft, selectedEntity]);

  const selectedEdge = useMemo(() => {
    if (selectedEntity?.type !== "edge" || !draft) return null;
    return draft.edges.find((edge) => edge.edgeId === selectedEntity.id) || null;
  }, [draft, selectedEntity]);

  const selectedGoal = useMemo(() => {
    if (selectedEntity?.type !== "goal" || !draft) return null;
    return (draft.goals || []).find((goal) => goal.goalId === selectedEntity.id) || null;
  }, [draft, selectedEntity]);

  // Map nodeId → goals[] for canvas badge rendering
  const nodeGoalMap = useMemo(() => {
    const map = new Map();
    (draft?.goals || []).forEach((goal) => {
      (goal.nodeIds || []).forEach((nodeId) => {
        if (!map.has(nodeId)) map.set(nodeId, []);
        map.get(nodeId).push(goal);
      });
    });
    return map;
  }, [draft?.goals]);

  const canvasSize = useMemo(() => {
    const nodes = draft?.nodes || [];
    const width = Math.max(
      2400,
      ...nodes.map((node) => Number(node.position?.x || 0) + NODE_WIDTH + 900)
    );
    const height = Math.max(
      1400,
      ...nodes.map((node) => Number(node.position?.y || 0) + NODE_HEIGHT + 360)
    );
    return { width, height };
  }, [draft?.nodes]);

  useEffect(() => {
    if (selectedEntity?.type === "node") {
      setOpenDrawer("node");
    } else if (selectedEntity?.type === "path" || selectedEntity?.type === "edge" || selectedEntity?.type === "goal") {
      setOpenDrawer("structure");
    }
  }, [selectedEntity?.type, selectedEntity?.id]);

  useEffect(() => {
    setGenerationPreviewContext(null);
    setGraphPreview({ steps: [], variables: {} });
    setPreviewPathId("");
    setGenerationPromptTemplate("");
    setSampleVariablesText("{}");
  }, [draft?.frameId]);

  useEffect(() => {
    let mounted = true;

    async function loadEditorData() {
      setLoading(true);
      setNotice({ type: "", text: "" });
      try {
        const requests = [
          fetch(`${apiBase}/api/admin/frames`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${apiBase}/api/admin/solution-graphs/validator-catalog`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ];
        if (!isCreate) {
          requests.push(
            fetch(`${apiBase}/api/admin/solution-graphs/${graphId}`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          );
        }

        const responses = await Promise.all(requests);
        const payloads = await Promise.all(responses.map((response) => response.json().catch(() => ({}))));

        if (!responses[0].ok) throw new Error(payloads[0].error || "Failed to load frames");
        if (!responses[1].ok) throw new Error(payloads[1].error || "Failed to load validator catalog");

        const loadedFrames = Array.isArray(payloads[0].frames) ? payloads[0].frames : [];
        const loadedCatalog = Array.isArray(payloads[1].catalog) ? payloads[1].catalog : [];

        if (!mounted) return;
        setFrames(loadedFrames);
        setCatalog(loadedCatalog);

        if (isCreate) {
          const isLibraryGraphCreate = location.state?.isLibraryGraph === true;
          const preferredFrameId = isLibraryGraphCreate
            ? ""
            : String(location.state?.frameId || "").trim() || String(loadedFrames[0]?._id || "").trim();
          const preferredFrame = isLibraryGraphCreate
            ? null
            : loadedFrames.find((frame) => String(frame._id) === preferredFrameId) || loadedFrames[0] || null;
          const blank = createBlankGraph(loadedCatalog, preferredFrame?._id || "", preferredFrame?.title || "", {
            isLibraryGraph: isLibraryGraphCreate,
            sourceFrame: preferredFrame
          });
          setDraft(blank);
          setSelectedEntity({
            type: "path",
            id: blank.paths[0]?.pathId || null
          });
        } else {
          if (!responses[2].ok) throw new Error(payloads[2].error || "Failed to load solution graph");
          const hydrated = hydrateGraph(payloads[2].graph, loadedCatalog);
          setDraft(hydrated);
          setSelectedEntity(
            hydrated?.nodes?.[0]
              ? { type: "node", id: hydrated.nodes[0].nodeId }
              : hydrated?.paths?.[0]
                ? { type: "path", id: hydrated.paths[0].pathId }
                : null
          );
        }
      } catch (err) {
        if (mounted) {
          setNotice({ type: "error", text: err.message || "Failed to load solution graph editor" });
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    if (token) {
      loadEditorData();
    }

    return () => {
      mounted = false;
    };
  }, [apiBase, token, graphId, isCreate, location.state]);

  function updateDraft(mutator) {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = typeof mutator === "function" ? mutator(prev) : prev;
      return {
        ...next,
        graphStats: {
          ...computeGraphStats(next),
          ...(next.graphStats?.publishReady ? { publishReady: next.graphStats.publishReady } : {})
        }
      };
    });
  }

  function setNodeField(nodeId, field, value) {
    // Special case: nodeId with "__goal__" prefix means update goal membership
    if (String(nodeId).startsWith("__goal__")) {
      const goalId = String(nodeId).slice("__goal__".length);
      setGoalField(goalId, field, value);
      return;
    }
    updateDraft((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node) => (node.nodeId === nodeId ? { ...node, [field]: value } : node))
    }));
  }

  function addGoal() {
    const nextGoal = createBlankGoal((draft?.goals?.length || 0) + 1);
    updateDraft((prev) => ({
      ...prev,
      goals: [...(prev.goals || []), nextGoal]
    }));
    setSelectedEntity({ type: "goal", id: nextGoal.goalId });
    setOpenDrawer("structure");
  }

  function setGoalField(goalId, field, value) {
    updateDraft((prev) => ({
      ...prev,
      goals: (prev.goals || []).map((goal) =>
        goal.goalId === goalId ? { ...goal, [field]: value } : goal
      )
    }));
  }

  function setNodeNestedField(nodeId, parentKey, childKey, value) {
    updateDraft((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node) =>
        node.nodeId === nodeId
          ? {
              ...node,
              [parentKey]: {
                ...(node[parentKey] || {}),
                [childKey]: value
              }
            }
          : node
      )
    }));
  }

  function setPathField(pathId, field, value) {
    updateDraft((prev) => {
      const nextPaths = prev.paths.map((path) =>
        path.pathId === pathId ? { ...path, [field]: value } : field === "isPrimaryReviewPath" && value ? { ...path, isPrimaryReviewPath: false } : path
      );
      return {
        ...prev,
        primaryReviewPathId:
          field === "isPrimaryReviewPath"
            ? value
              ? pathId
              : prev.primaryReviewPathId === pathId
                ? ""
                : prev.primaryReviewPathId
            : prev.primaryReviewPathId,
        paths: nextPaths
      };
    });
  }

  function setPathNestedField(pathId, parentKey, childKey, value) {
    updateDraft((prev) => ({
      ...prev,
      paths: prev.paths.map((path) =>
        path.pathId === pathId
          ? {
              ...path,
              [parentKey]: {
                ...(path[parentKey] || {}),
                [childKey]: value
              }
            }
          : path
      )
    }));
  }

  function setEdgeField(edgeId, field, value) {
    updateDraft((prev) => ({
      ...prev,
      edges: prev.edges.map((edge) => (edge.edgeId === edgeId ? { ...edge, [field]: value } : edge))
    }));
  }

  function addPath() {
    const nextPath = createBlankPath((draft?.paths?.length || 0) + 1);
    updateDraft((prev) => ({
      ...prev,
      primaryReviewPathId: prev.primaryReviewPathId || nextPath.pathId,
      paths: [...prev.paths, nextPath]
    }));
    setSelectedEntity({ type: "path", id: nextPath.pathId });
  }

  function addNode(templateKey = nodeTemplateKey) {
    if (!draft) return;
    const fallbackPathId = selectedPath?.pathId || draft.paths[0]?.pathId || "";
    const nextNode = createNodeFromTemplate(catalog, fallbackPathId, draft.nodes.length + 1, templateKey);
    updateDraft((prev) => ({
      ...prev,
      nodes: [...prev.nodes, nextNode]
    }));
    setSelectedEntity({ type: "node", id: nextNode.nodeId });
  }

  function duplicateSelectedNode() {
    if (!draft || !selectedNode) return;
    const nextNode = {
      ...selectedNode,
      nodeId: createClientId("node"),
      title: `${selectedNode.title || "Node"} Copy`,
      reviewOrder: Number.isFinite(Number(selectedNode.reviewOrder))
        ? Number(selectedNode.reviewOrder) + 1
        : draft.nodes.length + 1,
      position: {
        x: Number(selectedNode.position?.x || 0) + 32,
        y: Number(selectedNode.position?.y || 0) + 32
      }
    };
    updateDraft((prev) => ({
      ...prev,
      nodes: [...prev.nodes, nextNode]
    }));
    setSelectedEntity({ type: "node", id: nextNode.nodeId });
  }

  function addEdge() {
    if (!draft || draft.nodes.length < 2) {
      setNotice({ type: "error", text: "Add at least two nodes before creating an edge." });
      return;
    }
    const fromNodeId = selectedNode?.nodeId || draft.nodes[0]?.nodeId || "";
    const toNodeId =
      draft.nodes.find((node) => node.nodeId !== fromNodeId)?.nodeId || draft.nodes[1]?.nodeId || fromNodeId;
    const nextEdge = {
      edgeId: createClientId("edge"),
      fromNodeId,
      toNodeId,
      kind: "required",
      label: ""
    };
    updateDraft((prev) => ({
      ...prev,
      edges: [...prev.edges, nextEdge]
    }));
    setSelectedEntity({ type: "edge", id: nextEdge.edgeId });
  }

  function deleteSelectedEntity() {
    if (!draft || !selectedEntity) return;

    if (selectedEntity.type === "goal") {
      updateDraft((prev) => ({
        ...prev,
        goals: (prev.goals || []).filter((g) => g.goalId !== selectedEntity.id),
        paths: prev.paths.map((path) => ({
          ...path,
          goalSequence: (path.goalSequence || []).filter((id) => id !== selectedEntity.id)
        }))
      }));
      setSelectedEntity(null);
      return;
    }

    if (selectedEntity.type === "node") {
      updateDraft((prev) => ({
        ...prev,
        nodes: prev.nodes.filter((node) => node.nodeId !== selectedEntity.id),
        edges: prev.edges.filter(
          (edge) => edge.fromNodeId !== selectedEntity.id && edge.toNodeId !== selectedEntity.id
        ),
        goals: (prev.goals || []).map((goal) => ({
          ...goal,
          nodeIds: (goal.nodeIds || []).filter((id) => id !== selectedEntity.id)
        }))
      }));
    } else if (selectedEntity.type === "path") {
      const remainingPaths = draft.paths.filter((path) => path.pathId !== selectedEntity.id);
      updateDraft((prev) => ({
        ...prev,
        primaryReviewPathId:
          prev.primaryReviewPathId === selectedEntity.id ? remainingPaths[0]?.pathId || "" : prev.primaryReviewPathId,
        paths: remainingPaths.map((path, index) => ({
          ...path,
          isPrimaryReviewPath:
            prev.primaryReviewPathId === selectedEntity.id ? index === 0 : path.pathId === prev.primaryReviewPathId
        })),
        nodes: prev.nodes.map((node) => ({
          ...node,
          pathIds: (node.pathIds || []).filter((pathId) => pathId !== selectedEntity.id)
        }))
      }));
    } else if (selectedEntity.type === "edge") {
      updateDraft((prev) => ({
        ...prev,
        edges: prev.edges.filter((edge) => edge.edgeId !== selectedEntity.id)
      }));
    }

    setSelectedEntity(null);
  }

  function requestDeleteSelectedEntity() {
    if (!selectedEntity) return;
    const targetLabel =
      selectedEntity.type === "node"
        ? "this node"
        : selectedEntity.type === "path"
          ? "this path"
          : selectedEntity.type === "goal"
            ? "this goal"
            : "this edge";
    setDeleteConfirm({
      title: `Delete ${targetLabel}?`,
      message: `This will remove ${targetLabel} from the current graph draft.`,
      confirmLabel:
        selectedEntity.type === "node"
          ? "Delete Node"
          : selectedEntity.type === "path"
            ? "Delete Path"
            : selectedEntity.type === "goal"
              ? "Delete Goal"
              : "Delete Edge"
    });
  }

  function autoLayoutNodes() {
    updateDraft((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node, index) => ({
        ...node,
        position: {
          x: 48 + (index % 3) * 320,
          y: 48 + Math.floor(index / 3) * 220
        }
      }))
    }));
  }

  async function fetchGenerationPreviewContext(promptTemplateOverride = generationPromptTemplate, variablesOverride = null) {
    if (!draft?.frameId) {
      setNotice({ type: "error", text: "Select a frame before preparing graph generation." });
      return null;
    }

    const parsedVariables =
      variablesOverride ||
      (parsedSampleVariables.ok ? parsedSampleVariables.value : null);

    if (!parsedVariables) {
      setNotice({ type: "error", text: "Input variables JSON is invalid." });
      return null;
    }

    setGenerationPreviewLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/solution-graphs/generate-preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          frameId: draft.frameId,
          variables: parsedVariables,
          promptTemplate: promptTemplateOverride
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to load graph generation preview");
      }

      const preview = payload.preview || null;
      setGenerationPreviewContext(preview);
      setGenerationPromptTemplate(preview?.promptTemplate || promptTemplateOverride || "");
      setSampleVariablesText(JSON.stringify(preview?.variables || parsedVariables || {}, null, 2));
      setPreviewPathId((current) => current || preview?.compiledBaselineGraph?.primaryReviewPathId || "");
      return preview;
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to load graph generation preview" });
      return null;
    } finally {
      setGenerationPreviewLoading(false);
    }
  }

  async function openGenerationModal() {
    if (!draft?.frameId) {
      setNotice({ type: "error", text: "Select a frame before generating a draft." });
      return;
    }

    setGenerationModalOpen(true);
    if (!generationPreviewContext || String(generationPreviewContext.frameId || "") !== String(draft.frameId || "")) {
      await fetchGenerationPreviewContext(generationPromptTemplate);
    }
  }

  async function runGraphPreview(graphOverride = null) {
    if (!draft?.frameId) {
      setNotice({ type: "error", text: "Select a frame before previewing the graph." });
      return;
    }

    if (!parsedSampleVariables.ok) {
      setNotice({ type: "error", text: "Input variables JSON is invalid." });
      return;
    }

    const graphPayload =
      graphOverride ||
      ((draft?.nodes?.length || draft?.paths?.length) ? buildPayloadFromDraft(draft) : generationPreviewContext?.compiledBaselineGraph);

    if (!graphPayload) {
      setGraphPreview({ steps: [], variables: parsedSampleVariables.value });
      return;
    }

    setGraphPreviewLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/solution-graphs/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          frameId: draft.frameId,
          variables: parsedSampleVariables.value,
          pathId: previewPathId || "",
          graph: graphPayload
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to preview solution graph");
      }
      setGraphPreview(payload.preview || { steps: [], variables: parsedSampleVariables.value });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to preview solution graph" });
    } finally {
      setGraphPreviewLoading(false);
    }
  }

  function startDraggingNode(event, nodeId) {
    if (!canvasRef.current) return;
    const targetNode = draft?.nodes?.find((node) => node.nodeId === nodeId);
    if (!targetNode) return;
    event.preventDefault();

    dragRef.current = {
      nodeId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: Number(targetNode.position?.x || 0),
      startY: Number(targetNode.position?.y || 0),
      didDrag: false
    };

    function onPointerMove(moveEvent) {
      const drag = dragRef.current;
      if (!drag) return;
      const deltaX = moveEvent.clientX - drag.startClientX;
      const deltaY = moveEvent.clientY - drag.startClientY;
      if (!drag.didDrag && Math.hypot(deltaX, deltaY) >= 6) {
        drag.didDrag = true;
      }
      if (!drag.didDrag) return;
      updateDraft((prev) => ({
        ...prev,
        nodes: prev.nodes.map((node) =>
          node.nodeId === drag.nodeId
            ? {
                ...node,
                position: {
                  x: Math.max(16, drag.startX + deltaX),
                  y: Math.max(16, drag.startY + deltaY)
                }
              }
            : node
        )
      }));
    }

    function onPointerUp() {
      const drag = dragRef.current;
      if (drag?.didDrag) {
        suppressNodeClickRef.current = {
          nodeId: drag.nodeId,
          expiresAt: Date.now() + 250
        };
      }
      dragRef.current = null;
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    }

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  }

  function startPanningCanvas(event) {
    if (!canvasViewportRef.current) return;
    const button = typeof event.button === "number" ? event.button : 0;
    if (button !== 0) return;
    if (event.target.closest(".solution-graph-node-card")) return;

    event.preventDefault();
    event.stopPropagation();

    const viewport = canvasViewportRef.current;
    viewport.classList.add("is-panning");
    document.body.classList.add("solution-graph-is-panning");
    if (typeof viewport.setPointerCapture === "function") {
      try {
        viewport.setPointerCapture(event.pointerId);
      } catch (_) {
        // ignore pointer capture failures
      }
    }
    panRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startScrollLeft: viewport.scrollLeft,
      startScrollTop: viewport.scrollTop,
      didPan: false
    };

    function onPointerMove(moveEvent) {
      const pan = panRef.current;
      if (!pan || pan.pointerId !== moveEvent.pointerId) return;
      const deltaX = moveEvent.clientX - pan.startClientX;
      const deltaY = moveEvent.clientY - pan.startClientY;
      if (!pan.didPan && Math.hypot(deltaX, deltaY) >= 6) {
        pan.didPan = true;
      }
      if (!pan.didPan) return;
      moveEvent.preventDefault();
      viewport.scrollLeft = pan.startScrollLeft - deltaX;
      viewport.scrollTop = pan.startScrollTop - deltaY;
    }

    function endPan(endEvent) {
      const pan = panRef.current;
      if (!pan || pan.pointerId !== endEvent.pointerId) return;
      if (pan?.didPan) {
        suppressCanvasClickRef.current = Date.now() + 250;
      }
      viewport.classList.remove("is-panning");
      document.body.classList.remove("solution-graph-is-panning");
      if (typeof viewport.releasePointerCapture === "function") {
        try {
          viewport.releasePointerCapture(endEvent.pointerId);
        } catch (_) {
          // ignore pointer release failures
        }
      }
      panRef.current = null;
      viewport.removeEventListener("pointermove", onPointerMove);
      viewport.removeEventListener("pointerup", endPan);
      viewport.removeEventListener("pointercancel", endPan);
    }

    viewport.addEventListener("pointermove", onPointerMove);
    viewport.addEventListener("pointerup", endPan);
    viewport.addEventListener("pointercancel", endPan);
  }

  async function saveDraft(options = {}) {
    if (!draft?.frameId) {
      setNotice({ type: "error", text: "Select a frame before saving the graph." });
      return null;
    }

    const shouldForkPublished = Boolean(draft?._id && draft.status === "published");
    const shouldCreate = isCreate || !draft._id || shouldForkPublished;

    setSaving(true);
    if (!options.silentNotice) {
      setNotice({ type: "", text: "" });
    }
    try {
      const payload = buildPayloadFromDraft(draft);
      if (shouldForkPublished) {
        delete payload.version;
      }
      const response = await fetch(
        shouldCreate ? `${apiBase}/api/admin/solution-graphs` : `${apiBase}/api/admin/solution-graphs/${draft._id}`,
        {
          method: shouldCreate ? "POST" : "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );
      const responsePayload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responsePayload.error || "Failed to save solution graph");
      }
      const hydrated = hydrateGraph(responsePayload.graph, catalog);
      setDraft(hydrated);
      if (!options.silentNotice) {
        setNotice({
          type: "success",
          text: shouldForkPublished
            ? "Created a new draft version from the published graph."
            : "Solution graph draft saved."
        });
      }
      if ((shouldCreate || !graphId) && hydrated?._id) {
        navigate(`/admin/solution-graphs/${hydrated._id}`, { replace: true, state: { graph: hydrated } });
      }
      return hydrated;
    } catch (err) {
      if (!options.silentNotice) {
        setNotice({ type: "error", text: err.message || "Failed to save solution graph" });
      }
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function generateDraft(options = {}) {
    if (!draft?.frameId) {
      setNotice({ type: "error", text: "Select a frame before generating a draft." });
      return;
    }

    const parsedVariables = options.variables || (parsedSampleVariables.ok ? parsedSampleVariables.value : null);
    if (!parsedVariables) {
      setNotice({ type: "error", text: "Input variables JSON is invalid." });
      return;
    }

    setGenerating(true);
    setNotice({ type: "", text: "" });
    try {
      const response = await fetch(`${apiBase}/api/admin/solution-graphs/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          frameId: draft.frameId,
          variables: parsedVariables,
          promptTemplate: options.promptTemplate || generationPromptTemplate || undefined
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to generate solution graph draft");
      }

      const generatedGraph = hydrateGraph(payload.generated?.graph, catalog);
      if (!generatedGraph) {
        throw new Error("Generated draft was empty.");
      }

      setDraft((prev) => ({
        ...prev,
        ...generatedGraph,
        _id: prev?._id || null,
        frameId: prev?.frameId || generatedGraph.frameId || "",
        version: prev?.version || generatedGraph.version || 1,
        status: prev?._id ? prev.status : generatedGraph.status
      }));
      setSelectedEntity(
        generatedGraph.nodes?.[0]
          ? { type: "node", id: generatedGraph.nodes[0].nodeId }
          : generatedGraph.paths?.[0]
            ? { type: "path", id: generatedGraph.paths[0].pathId }
            : null
      );
      const warningCount = payload.generated?.validation?.warnings?.length || 0;
      setGenerationModalOpen(false);
      setGraphPreview({ steps: [], variables: parsedVariables });
      setNotice({
        type: "success",
        text:
          payload.generated?.source === "compiled"
            ? `Baseline graph draft loaded${warningCount ? ` with ${warningCount} review notes.` : "."}`
            : `AI graph draft loaded${warningCount ? ` with ${warningCount} review notes.` : "."}`
      });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to generate solution graph draft" });
    } finally {
      setGenerating(false);
    }
  }

  async function validateGraph() {
    let graphToValidate = draft;
    if (!draft?._id || draft.status === "published") {
      const saved = await saveDraft({ silentNotice: true });
      if (!saved?._id) {
        return;
      }
      graphToValidate = saved;
    }

    setValidating(true);
    setNotice({ type: "", text: "" });
    try {
      const response = await fetch(`${apiBase}/api/admin/solution-graphs/${graphToValidate._id}/validate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to validate solution graph");
      }
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              validationErrors: payload.validation?.errors || [],
              validationWarnings: payload.validation?.warnings || [],
              graphStats: payload.validation?.stats || prev.graphStats
            }
          : prev
      );
      setNotice({
        type: payload.validation?.publishReady ? "success" : "error",
        text: payload.validation?.publishReady
          ? "Graph is publish-ready."
          : "Validation completed. Review the remaining issues."
      });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to validate solution graph" });
    } finally {
      setValidating(false);
    }
  }

  async function publishGraph() {
    const saved = await saveDraft({ silentNotice: true });
    if (!saved?._id) {
      return;
    }
    const graphToPublish = saved;

    setPublishing(true);
    setNotice({ type: "", text: "" });
    try {
      const response = await fetch(`${apiBase}/api/admin/solution-graphs/${graphToPublish._id}/publish`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to publish solution graph");
      }
      const hydrated = hydrateGraph(payload.graph, catalog);
      setDraft(hydrated);
      setNotice({ type: "success", text: "Solution graph published." });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to publish solution graph" });
    } finally {
      setPublishing(false);
    }
  }

  const currentRuleDefinition = useMemo(() => {
    if (!selectedNode) return { group: null, rule: null };
    return findRuleDefinition(catalog, selectedNode.validator?.type, selectedNode.validator?.ruleId);
  }, [catalog, selectedNode]);
  const nodeTemplateOptions = useMemo(() => buildNodeTemplateOptions(catalog), [catalog]);
  const parsedSampleVariables = useMemo(() => parseJsonText(sampleVariablesText, {}), [sampleVariablesText]);

  const graphStats = useMemo(() => computeGraphStats(draft || {}), [draft]);

  function setNodeValidator(node, nextKind) {
    const nextValidator = buildDefaultValidator(nextKind, catalog);
    updateDraft((prev) => ({
      ...prev,
      nodes: prev.nodes.map((item) =>
        item.nodeId === node.nodeId
          ? {
              ...item,
              kind: nextKind,
              requiredForProgress:
                nextKind === "deterministic" && isSupportingDeterministicRule(nextValidator.type, nextValidator.ruleId)
                  ? false
                  : item.requiredForProgress !== false,
              anchor: {
                ...item.anchor,
                stepTag:
                  nextKind === "canonical"
                    ? item.anchor?.stepTag || "other"
                    : isSupportingDeterministicRule(nextValidator.type, nextValidator.ruleId)
                    ? "assumption"
                    : ""
              },
              validator: nextValidator
            }
          : item
      )
    }));
  }

  function setNodeAnchorStepTag(nodeId, value) {
    updateDraft((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node) => {
        if (node.nodeId !== nodeId) return node;
        const nextValidator =
          node.kind === "canonical" &&
          node.validator?.type === "equivalence" &&
          node.validator?.ruleId === "expression_anchor"
            ? {
                ...node.validator,
                params: {
                  ...(node.validator?.params || {}),
                  stepTag: value
                }
              }
            : node.validator;
        return {
          ...node,
          anchor: {
            ...(node.anchor || {}),
            stepTag: value
          },
          validator: nextValidator
        };
      })
    }));
  }

  if (loading || !draft) {
    return (
      <div className="admin-panel">
        <div className="admin-editor">Loading solution graph editor...</div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-page-header">
        <div>
          <div className="page-title">{isCreate ? "New Solution Graph" : draft.title || "Solution Graph"}</div>
          <p className="page-sub">
            Mixed canonical and deterministic nodes, validator-driven authoring, and path-aware editing.
          </p>
        </div>
        <div className="solution-graph-header-actions">
          <Link className="auth-link" to="/admin/solution-graphs">
            Back to Graphs
          </Link>
          <button className="primary-button" type="button" onClick={saveDraft} disabled={saving}>
            {saving ? "Saving..." : "Save Draft"}
          </button>
          <button className="primary-button" type="button" onClick={publishGraph} disabled={publishing}>
            {publishing ? "Publishing..." : "Publish"}
          </button>
        </div>
      </div>

      {notice.text && (
        <div className={`admin-notice ${notice.type === "error" ? "error" : "success"}`}>{notice.text}</div>
      )}

      <SolutionGraphDashboard
        draft={draft}
        frames={frames}
        selectedFrame={selectedFrame}
        updateDraft={updateDraft}
        graphStats={graphStats}
      />

      <SolutionGraphWorkspace
        draft={draft}
        selectedEntity={selectedEntity}
        selectedNode={selectedNode}
        selectedPath={selectedPath}
        selectedEdge={selectedEdge}
        selectedGoal={selectedGoal}
        setSelectedEntity={setSelectedEntity}
        canvasViewportRef={canvasViewportRef}
        canvasRef={canvasRef}
        canvasSize={canvasSize}
        nodeTemplateOptions={nodeTemplateOptions}
        nodeTemplateKey={nodeTemplateKey}
        setNodeTemplateKey={setNodeTemplateKey}
        addNode={addNode}
        duplicateSelectedNode={duplicateSelectedNode}
        startDraggingNode={startDraggingNode}
        suppressNodeClickRef={suppressNodeClickRef}
        startPanningCanvas={startPanningCanvas}
        suppressCanvasClickRef={suppressCanvasClickRef}
        openDrawer={openDrawer}
        setOpenDrawer={setOpenDrawer}
        generateDraft={openGenerationModal}
        generating={generating}
        autoLayoutNodes={autoLayoutNodes}
        validateGraph={validateGraph}
        validating={validating}
        catalog={catalog}
        setNodeField={setNodeField}
        setNodeNestedField={setNodeNestedField}
        setNodeAnchorStepTag={setNodeAnchorStepTag}
        setPathField={setPathField}
        setPathNestedField={setPathNestedField}
        setEdgeField={setEdgeField}
        setGoalField={setGoalField}
        setNodeValidator={setNodeValidator}
        addPath={addPath}
        addEdge={addEdge}
        addGoal={addGoal}
        deleteSelectedEntity={requestDeleteSelectedEntity}
        currentRuleDefinition={currentRuleDefinition}
        nodeGoalMap={nodeGoalMap}
        apiBase={apiBase}
        token={token}
      />

      <SolutionGraphGenerationModal
        open={generationModalOpen}
        closeModal={() => setGenerationModalOpen(false)}
        selectedFrame={selectedFrame}
        previewContext={generationPreviewContext}
        loadingContext={generationPreviewLoading}
        sampleVariablesText={sampleVariablesText}
        setSampleVariablesText={setSampleVariablesText}
        parsedSampleVariables={parsedSampleVariables}
        promptTemplate={generationPromptTemplate}
        setPromptTemplate={setGenerationPromptTemplate}
        refreshPreviewContext={() => fetchGenerationPreviewContext(generationPromptTemplate)}
        generateDraft={() => generateDraft({ variables: parsedSampleVariables.value, promptTemplate: generationPromptTemplate })}
        generating={generating}
        runGraphPreview={() => runGraphPreview()}
        graphPreview={graphPreview}
        graphPreviewLoading={graphPreviewLoading}
        previewPathId={previewPathId}
        setPreviewPathId={setPreviewPathId}
        previewGraph={(draft?.nodes?.length || draft?.paths?.length) ? buildPayloadFromDraft(draft) : generationPreviewContext?.compiledBaselineGraph}
      />
      <ConfirmDialog
        open={Boolean(deleteConfirm)}
        title={deleteConfirm?.title || "Delete item?"}
        message={deleteConfirm?.message || "This action cannot be undone."}
        confirmLabel={deleteConfirm?.confirmLabel || "Delete"}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => {
          deleteSelectedEntity();
          setDeleteConfirm(null);
        }}
      />
    </div>
  );
}

export default AdminSolutionGraphEditor;
